import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LiveAvatarSession, SessionEvent, SessionState, AgentEventsEnum } from '@heygen/liveavatar-web-sdk';
import { Send, X, ChevronUp, ChevronDown, Mic, MicOff, Info } from 'lucide-react';
import { DISCLAIMER_TEXT } from './LegalDisclaimer';
import { isActiveVoiceState } from './voiceState';
import UserJourney, { STORAGE_KEY as USER_JOURNEY_STORAGE_KEY } from './UserJourney';
import { findWakePhrase, extractAfterWake } from './wakeWord';
import { pushDataLayerEvent, trackButtonClick } from '../utils/tracking';
import './AvatarComponent.css';
import './LegalDisclaimer.css';

// App branding
const APP_NAME = 'Afterlife Bot';
const APP_SUBTITLE = 'Speak with Mr.Edward Berwind of the Elms';
const FOOTER_TEXT = 'A conversational reenactment featuring Mr.Edward Berwind of the Elms of Newport, RI. Responses are for entertainment and educational purposes.';
const LOGO_URL = 'https://actiontourguide.com/wp-content/uploads/2023/03/image.png';
// Display name in chat header and for saved session analysis
const AVATAR_DISPLAY_NAME = 'Mr.Edward Berwind';
const SESSION_SAVE_NAME = APP_NAME; // Tag saved sessions for DB analysis

// Do not hardcode real avatar/voice IDs in client code (Netlify secret scanning will fail builds).
// These placeholders are only fallback values if /api/config and env variables are unavailable.
const DEFAULT_AVATAR_ID = '00000000-0000-0000-0000-000000000000';
const DEFAULT_VOICE_ID = '00000000-0000-0000-0000-000000000000';
const AVATAR_OUTPUT_VOLUME = 0.9;
const SESSION_IDLE_MS = 3 * 60 * 1000; // End session after 3 minutes with no user activity
const OPENING_GREETING = "Hello! I’m Edward Berwind, and I am pleased to receive your questions. Speak freely in your natural voice; you may interrupt me at any time for a more natural exchange. If you are speaking with others nearby, please mute so I do not interrupt your conversation.";

// When true, the mic auto-mutes (silently, via the software gate) while the avatar
// speaks so background/side conversations aren't captured. It auto-unmutes when the
// avatar finishes. The user can tap Unmute mid-speech to barge in and interrupt.
const AUTO_MUTE_WHILE_SPEAKING = true;

const isDev = process.env.NODE_ENV === 'development';
const log = (...args) => { if (isDev) console.log(...args); };
const warn = (...args) => { if (isDev) console.warn(...args); };

// Mr. Edward Berwind's varied wake responses — cycles through each session so it never feels robotic.
const WAKE_GREETINGS = [
  'Yes? How may I help you?',
  'You have my attention. Please speak.',
  'What weighs upon your mind?',
  'I am listening. What would you know?',
  'Speak freely. What is your question?',
  'Yes, I hear you. How may I assist?',
  'Come now, what is it you wish to know?',
  'Proceed. I am all ears.',
  'You have found me. What can I do for you?',
  'I am here. What troubles you today?',
];

// Normalize chat text to sentence case for cleaner message display.
const toSentenceCase = (text) => {
  const input = String(text || '').trim();
  if (!input) return '';
  const lower = input.toLowerCase();
  return lower.replace(/(^\s*[a-z])|([.!?]\s+[a-z])|(\n\s*[a-z])/g, (match) =>
    match.toUpperCase()
  );
};

function AvatarComponent() {
  // Configuration – from server /api/config (AVATAR_ID, VOICE_ID in .env) or frontend env or defaults
  const [avatarID, setAvatarID] = useState(process.env.REACT_APP_AVATAR_ID || DEFAULT_AVATAR_ID);
  const [voiceID, setVoiceID] = useState(process.env.REACT_APP_VOICE_ID || DEFAULT_VOICE_ID);
  // Use same-origin proxy to avoid CORS: browser calls our backend, backend calls LiveAvatar
  // Call LiveAvatar API directly (no proxy). If you see CORS errors, restore the proxy in server.js and api/liveavatar/
  const API_URL = 'https://api.liveavatar.com';
  
  // State
  const [chatInput, setChatInput] = useState('');
  const [sessionActive, setSessionActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isEndingSession, setIsEndingSession] = useState(false);
  const [isTalking, setIsTalking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isActivelyListening, setIsActivelyListening] = useState(false); // Track if we're actively listening (mic button clicked)
  const [isUserSpeaking, setIsUserSpeaking] = useState(false); // Derived from SR events (best-effort)
  const [, setMode] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  // Mobile: default full avatar (chat panel hidden); desktop: chat panel visible
  const [chatPanelVisible, setChatPanelVisible] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? false : true
  );
  const [mobileChatExpanded, setMobileChatExpanded] = useState(false); // false = split view (video top, chat card bottom), true = full-screen chat
  const [visualViewportStyle, setVisualViewportStyle] = useState(null); // mobile: { top, left, width, height } so layout stays fixed when keyboard opens
  const [showLegalBeforeStart, setShowLegalBeforeStart] = useState(false); // Show legal modal every time before starting session
  const [legalConsentChecked, setLegalConsentChecked] = useState(false); // Require consent before starting session
  const [videoReady, setVideoReady] = useState(false); // True once remote video track is attached and playing
  const [isThinking, setIsThinking] = useState(false); // True while waiting for AI response
  const [isMicMuted, setIsMicMuted] = useState(false); // User-controlled mic mute
  const [isMicPermissionDenied, setIsMicPermissionDenied] = useState(false); // True when browser blocks microphone
  const [hasMicDevice, setHasMicDevice] = useState(true); // False when no microphone input device is present
  const [voicePermissionHelpVisible, setVoicePermissionHelpVisible] = useState(false); // iOS: show when stuck in Active without receiving speech
  const [, setVoicePermissionHelpType] = useState('both'); // 'microphone' | 'speech-recognition' | 'both'
  const [showUserJourney, setShowUserJourney] = useState(false);
  const [userJourneyMode, setUserJourneyMode] = useState('short'); // short = welcome-only; full = full 7-step
  const [showInfoMenu, setShowInfoMenu] = useState(false);
  const [infoPopupPath, setInfoPopupPath] = useState('');
  /** Android SR debug: event log lines (dev or REACT_APP_ANDROID_SR_DEBUG=true). */
  const [, setAndroidSrDebugLines] = useState([]);
  /** Android SR debug: ref snapshot refreshed ~1s (session, SR active, wake, etc.). */
  const [, setAndroidSrDebugSnapshot] = useState(null);

  // iOS: Safari blocks unmuted autoplay until user gesture; show "Tap to hear avatar" only on iOS
  const isIOS = typeof navigator !== 'undefined' && (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
  const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
  const isMotoAndroid = Boolean(isAndroid && /moto|motorola/i.test(String(navigator?.userAgent || '')));
  // Audio-monitor gating reduces Android mic on/off sounds, but can prevent SR from starting
  // on some devices. Make it opt-in for production reliability.
  const ANDROID_USE_AUDIO_MONITOR = process.env.REACT_APP_ANDROID_USE_AUDIO_MONITOR === 'true';
  // Android only: second mic stream for OS beep suppression. Default ON — without it,
  // every SpeechRecognition restart (Android kills SR every ~5 s on no-speech) plays the
  // OS mic on/off chime, causing random beeping during sessions.
  // Set REACT_APP_ANDROID_MIC_KEEPALIVE=false to disable if Web Speech breaks on a device.
  const ANDROID_MIC_KEEPALIVE_ENABLED = isAndroid && process.env.REACT_APP_ANDROID_MIC_KEEPALIVE !== 'false';
  // Bottom debug strip: development builds, or set REACT_APP_ANDROID_SR_DEBUG=true (e.g. Vercel) for device testing.
  const ANDROID_SR_DEBUG_UI = Boolean(
    isAndroid && (isDev || process.env.REACT_APP_ANDROID_SR_DEBUG === 'true')
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (showLegalBeforeStart) {
      setLegalConsentChecked(false);
    }
  }, [showLegalBeforeStart]);

  const androidSrDebugLog = useCallback((msg) => {
    if (!ANDROID_SR_DEBUG_UI) return;
    console.warn('[Android SR]', msg);
    const ts = new Date().toISOString().slice(11, 23);
    setAndroidSrDebugLines((prev) => [...prev.slice(-24), `${ts} ${msg}`]);
  }, [ANDROID_SR_DEBUG_UI]);

  // Older iOS Safari often delays or never emits "final" SpeechRecognition results.
  // Enable a conservative fallback that can accept longer interim phrases as queries.
  const iOSMajorVersion = (() => {
    if (typeof navigator === 'undefined') return null;
    const ua = String(navigator.userAgent || '');
    const m = ua.match(/OS (\d+)_\d+(_\d+)? like Mac OS X/i);
    if (!m) return null;
    const v = Number(m[1]);
    return Number.isFinite(v) ? v : null;
  })();
  const isLegacyIOSSafari = Boolean(isIOS && iOSMajorVersion != null && iOSMajorVersion <= 16);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  
  // Refs
  const avatarInstanceRef = useRef(null);
  const videoRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatMessagesContainerRef = useRef(null);
  const sessionIdRef = useRef(null); // Store session_id for API calls
  const sessionTokenRef = useRef(null); // Store session token for Authorization header
  const isActivelyListeningRef = useRef(false); // Ref to track active listening state for event handlers
  const recognitionRef = useRef(null); // Ref for browser Speech Recognition API
  const sessionStartTimeRef = useRef(null); // When session became active (for saving history)
  const hasSavedThisSessionRef = useRef(false); // Prevent duplicate save on End Chat
  const isClosingRef = useRef(false); // Prevent duplicate close handler runs
  const isStreamReadyRef = useRef(false); // Track stream readiness for greeting
  const isConnectedRef = useRef(false); // Track connected state for greeting
  const hasSpokenGreetingRef = useRef(false); // Ensure greeting is spoken once per session
  const messageIdRef = useRef(0); // Unique id for each message (avoids Date.now() key collisions)
  const prevSessionActiveRef = useRef(false); // Track session active transition so we only switch to chat when session ends
  const ttsAudioRef = useRef(null); // No longer used for ElevenLabs; kept for safe cleanup
  const sessionActiveRef = useRef(false);
  const isMicMutedRef = useRef(false);
  // Silent mute mechanism: recognition keeps running continuously; while the gate is
  // closed we discard recognition results instead of stopping/restarting SR (which
  // would make the phone play its mic-focus beep on every mute/unmute).
  const micGateRef = useRef(false);            // true = discard recognition results (silent mute)
  const manualMuteRef = useRef(false);         // true only when the USER muted via the button
  const autoMutedForSpeechRef = useRef(false); // true while auto-muted because the avatar is speaking
  const isAvatarTalkingRef = useRef(false);
  const pendingGoodbyeDisconnectRef = useRef(false);
  const chatMessagesRef = useRef([]);
  const handleCloseRef = useRef(null);
  const lastSpeechResultTimeRef = useRef(null); // Detect when stuck in Active without receiving speech (iOS)
  const hasReceivedAnySpeechRef = useRef(false); // Track if we've ever gotten a recognition result this session
  const activeListeningStartTimeRef = useRef(null); // When we entered Active/Listening
  const micWarmupDoneRef = useRef(false); // Ensure we only prompt for mic permission once
  const voicePermissionHelpShownRef = useRef(false); // Prevent repeating popup + speech
  const lastMicGrantedAtRef = useRef(0); // Cooldown: don't assume SR is blocked right after user grants mic
  const micPermissionStateRef = useRef('unknown'); // 'unknown' | 'granted' | 'denied' – for correct permission popup
  const voiceAbortControllerRef = useRef(null); // Abort voice input when user taps to interrupt
  const chatAbortControllerRef = useRef(null); // Abort chat request when user taps to interrupt
  const glowCleanupRef = useRef(null); // Cleanup fn for WebAudio glow analyzer
  const wakeActiveRef = useRef(false); // True after "hey Mr. Edward Berwind" until we submit the question
  const wakeCoalesceRef = useRef({ timer: null, transcripts: [] });
  const recentSpeechRef = useRef(''); // Rolling buffer to catch wake phrase split across utterances
  const wasSessionActiveRef = useRef(false); // For end-of-session UI cleanup
  const wakeCountRef = useRef(0); // How many times wake fired this session (for greeting rotation)
  const submitWakeQueryRef = useRef(null); // Stable pointer to latest submit fn (used by SR/timer callbacks)
  const isResponseInFlightRef = useRef(false); // Prevent overlapping /api/chat requests causing duplicate answers
  const lastSubmittedUserTextRef = useRef(''); // Deduplicate rapid duplicate SR submits
  const lastSubmittedCompactTextRef = useRef(''); // Stronger dedupe against interim/final variants
  const lastSubmittedAtRef = useRef(0);
  const micKeepAliveRef = useRef(null); // Android: silent getUserMedia stream held open to suppress mic on/off sounds
  const audioMonitorRef = useRef(null); // Android: RAF id for audio-level SR gating (post-wake)
  const sessionWakeActivatedRef = useRef(false); // Android: true after first wake word — enables audio-gated SR
  /** Android: coalesce rapid onend (silence/no-speech) into one restart to cut OS mic sounds. */
  const androidSrOnEndDebounceTimerRef = useRef(null);
  const avatarAudioRecoveryTimeoutRef = useRef(null); // Debounced retry to recover occasional muffled playback
  const speakWatchdogRef = useRef(null); // Safety reset if SPEAK_ENDED event is missed
  const showListeningAfterGreetingRef = useRef(false); // One-time UX hint after opening greeting
  const userSpeakingTimeoutRef = useRef(null); // Debounce clear for isUserSpeaking
  const sessionIdleTimerRef = useRef(null);
  const lastSessionActivityRef = useRef(0);
  const isThinkingRef = useRef(false);
  const isUserSpeakingRef = useRef(false);
  const touchSessionActivityRef = useRef(() => {});

  // Gate SR behind mic-level monitor only before wake; after greeting / wake, always run SR or users stay silent forever.
  const androidShouldGateSrWithAudioMonitor = () =>
    isAndroid && ANDROID_USE_AUDIO_MONITOR && !isMotoAndroid && !wakeActiveRef.current;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    sessionActiveRef.current = sessionActive;
  }, [sessionActive]);

  useEffect(() => {
    isThinkingRef.current = isThinking;
  }, [isThinking]);

  useEffect(() => {
    isUserSpeakingRef.current = isUserSpeaking;
  }, [isUserSpeaking]);

  // Android SR debug: live ref snapshot for on-screen strip + remote debugging.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!ANDROID_SR_DEBUG_UI) return;
    const tick = () => {
      setAndroidSrDebugSnapshot({
        session: !!sessionActiveRef.current,
        muted: !!isMicMutedRef.current,
        wake: !!wakeActiveRef.current,
        srActive: !!isActivelyListeningRef.current,
        uiListen: isListening,
        userSpeak: isUserSpeaking,
        avatarTalk: !!isAvatarTalkingRef.current,
        hasRec: !!recognitionRef.current,
        audioMon: !!audioMonitorRef.current,
        micKA: !!micKeepAliveRef.current,
        thinking: isThinking,
      });
    };
    tick();
    const id = setInterval(tick, 900);
    return () => clearInterval(id);
  }, [ANDROID_SR_DEBUG_UI, isListening, isUserSpeaking, isThinking]);

  useEffect(() => {
    if (!ANDROID_SR_DEBUG_UI) return;
    if (!sessionActive) {
      setAndroidSrDebugLines([]);
      setAndroidSrDebugSnapshot(null);
    }
  }, [ANDROID_SR_DEBUG_UI, sessionActive]);

  useEffect(() => {
    return () => {
      if (userSpeakingTimeoutRef.current) {
        clearTimeout(userSpeakingTimeoutRef.current);
        userSpeakingTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!navigator?.mediaDevices?.enumerateDevices) return undefined;

    const refreshMicAvailability = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasAudioInput = devices.some((d) => d.kind === 'audioinput');
        setHasMicDevice(hasAudioInput);
      } catch (_) {
        // Keep previous state if device enumeration is blocked/unavailable.
      }
    };

    refreshMicAvailability();
    const onDeviceChange = () => {
      refreshMicAvailability();
    };
    navigator.mediaDevices.addEventListener?.('devicechange', onDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener?.('devicechange', onDeviceChange);
    };
  }, []);

  const initGlowAnalyzer = (videoElement) => {
    if (!videoElement) return () => {};
    if (typeof window === 'undefined') return () => {};
    if (!window.AudioContext && !window.webkitAudioContext) return () => {};

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioCtx();
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.75;

    let source;
    // Prefer MediaStreamSource for WebRTC video (createMediaElementSource fails on srcObject streams).
    // createMediaStreamSource only taps the stream for analysis — the video element keeps playing normally.
    const stream = videoElement.srcObject;
    if (stream && typeof stream.getAudioTracks === 'function' && stream.getAudioTracks().length > 0) {
      try {
        source = audioCtx.createMediaStreamSource(stream);
        source.connect(analyser);
        // Do NOT connect analyser → destination; video element already handles playback.
      } catch (e) {
        try { audioCtx.close(); } catch (_) {}
        return () => {};
      }
    } else {
      // Fallback for <video src="..."> (non-WebRTC).
      try {
        source = audioCtx.createMediaElementSource(videoElement);
        source.connect(analyser);
        analyser.connect(audioCtx.destination);
      } catch (e) {
        try { audioCtx.close(); } catch (_) {}
        return () => {};
      }
    }

    if (audioCtx.state === 'suspended') {
      audioCtx.resume().catch(() => {});
    }

    const data = new Uint8Array(analyser.frequencyBinCount);
    let rafId = null;
    let stopped = false;

    const tick = () => {
      if (stopped) return;
      analyser.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i];
      const avg = sum / (data.length || 1); // 0–255
      const normalized = Math.min(1, Math.max(0, avg / 70)); // speech energy threshold
      const intensity = 1.0 + normalized * 0.45; // 1.0–1.45
      document.body?.style?.setProperty('--glow-intensity', intensity.toFixed(3));
      rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);

    return () => {
      stopped = true;
      if (rafId) window.cancelAnimationFrame(rafId);
      try { source.disconnect(); } catch (_) {}
      try { analyser.disconnect(); } catch (_) {}
      try { audioCtx.close(); } catch (_) {}
    };
  };

  // Audio analyser: drives --glow-intensity so the ring pulses with the avatar's voice.
  // NOTE: The ring is now rendered as a real DOM <div> in JSX (not body pseudo-elements)
  // so it works correctly on Android Chrome where body::before with position:fixed is broken.
  useEffect(() => {
    if (!isTalking) {
      document.body.style.setProperty('--glow-intensity', '1');
      glowCleanupRef.current?.();
      glowCleanupRef.current = null;
      return;
    }
    const el = videoRef.current;
    if (el) {
      glowCleanupRef.current?.();
      glowCleanupRef.current = initGlowAnalyzer(el);
    }
    return () => {};
  }, [isTalking]);

  // Clean up glow analyser when session ends.
  useEffect(() => {
    if (!sessionActive) {
      document.body.style.setProperty('--glow-intensity', '1');
      glowCleanupRef.current?.();
      glowCleanupRef.current = null;
    }
  }, [sessionActive]);

  // Silent auto-mute driven by the visible "speaking" state (isTalking), NOT by the
  // SDK's AVATAR_SPEAK_STARTED/ENDED events. The app speaks via session.repeat()
  // (raw TTS), which does not reliably emit those agent-speak events — especially on
  // iOS — so wiring auto-mute to them left it never firing. isTalking is set by
  // makeAvatarSpeak the moment the avatar starts and cleared by the same signal the
  // rest of the UI already uses when it stops, so this stays in sync with "Speaking…".
  // Mute here is the silent software gate (discard results); recognition is never
  // stopped/restarted, so there is no mic beep.
  useEffect(() => {
    if (!AUTO_MUTE_WHILE_SPEAKING) return;
    if (!sessionActive) return;
    if (isTalking) {
      if (manualMuteRef.current) return; // user's explicit mute wins over auto-mute
      autoMutedForSpeechRef.current = true;
      micGateRef.current = true;
      isMicMutedRef.current = true;
      setIsMicMuted(true);
      // Drop any half-captured speech so it isn't submitted after the avatar finishes.
      const coalesce = wakeCoalesceRef.current;
      if (coalesce?.timer) clearTimeout(coalesce.timer);
      if (coalesce) { coalesce.timer = null; coalesce.transcripts = []; }
    } else if (autoMutedForSpeechRef.current && !manualMuteRef.current) {
      // Avatar finished and we auto-muted for it — silently reopen the gate.
      autoMutedForSpeechRef.current = false;
      micGateRef.current = false;
      isMicMutedRef.current = false;
      setIsMicMuted(false);
    }
  }, [isTalking, sessionActive]);

  // Ensure speech recognition is running whenever a session is active (same retry path for iOS and Android).
  useEffect(() => {
    if (!sessionActive) return;
    if (isMicMuted) return;
    // Small delay avoids racing the stream attach on some mobile browsers.
    // Also retry a few times since some browsers silently fail the first start().
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 8;

    const tryStart = () => {
      if (cancelled) return;
      if (!sessionActiveRef.current || isMicMutedRef.current) return;
      attempts += 1;
      try { androidShouldGateSrWithAudioMonitor() ? startAudioMonitoring() : startSpeechRecognition(); } catch (_) {}
      // On Android the audio monitor is self-sustaining; only retry on other browsers.
      if (!isAndroid && attempts < MAX_ATTEMPTS) {
        setTimeout(tryStart, 450);
      }
    };

    const t = setTimeout(tryStart, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [sessionActive, isMicMuted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show onboarding only once (first run). Users can reopen via the help button.
  useEffect(() => {
    try {
      const seen = window.localStorage.getItem(USER_JOURNEY_STORAGE_KEY) === 'true';
      if (!seen) {
        setUserJourneyMode('short');
        setShowUserJourney(true);
      }
    } catch (_) {}
  }, []);

  // Close onboarding when a session starts/ends so it never lingers over the avatar.
  useEffect(() => {
    const was = wasSessionActiveRef.current;
    wasSessionActiveRef.current = sessionActive;
    if (sessionActive) {
      setShowUserJourney(false);
      return;
    }
    if (was && !sessionActive) {
      setShowUserJourney(false);
    }
  }, [sessionActive]);

  useEffect(() => {
    if (!voicePermissionHelpVisible) {
      voicePermissionHelpShownRef.current = false;
    }
  }, [voicePermissionHelpVisible]);

  // iOS/Android: Detect when stuck in "Active" without receiving any speech – show permission help
  useEffect(() => {
    if (!sessionActive || isMicMuted || (!isIOS && !isAndroid)) return;
    const interval = setInterval(() => {
      if (!sessionActiveRef.current || isMicMutedRef.current) return;
      if (isAvatarTalkingRef.current) return; // Only show after avatar has finished speaking
      if (hasReceivedAnySpeechRef.current) return; // User has spoken successfully
      // Do not show the popup while recognition is running (background wake-word or active listening).
      if (isActivelyListeningRef.current) return;
      if (isListening) return;
      const start = activeListeningStartTimeRef.current;
      if (!start) return;
      const elapsed = Date.now() - start;
      // If we've been truly stuck (recognition not running) for more than 8 seconds,
      // show the permission help popup.
      if (elapsed >= 8000) {
        showVoicePermissionHelp('both');
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [sessionActive, isMicMuted, isIOS, isAndroid, isListening]); // eslint-disable-line react-hooks/exhaustive-deps

  // Android/iOS recovery: sometimes SpeechRecognition is "running" but produces no results
  // (mic enabled, but user speech not captured). This watchdog restarts listening safely.
  useEffect(() => {
    if (!sessionActive || isMicMuted) return;
    let lastKickAt = 0;
    const interval = setInterval(() => {
      if (!sessionActiveRef.current || isMicMutedRef.current) return;
      if (isAvatarTalkingRef.current) return;
      const start = activeListeningStartTimeRef.current;
      if (!start) return;
      const elapsed = Date.now() - start;
      // If SR/monitor has been active for a while but no speech results ever arrived, restart.
      if (!hasReceivedAnySpeechRef.current && elapsed >= 7000) {
        const now = Date.now();
        if (now - lastKickAt < 8000) return; // avoid flapping
        lastKickAt = now;
        try {
          // Soft stop: keep keep-alive stream if present (reduces Android OS sounds).
          stopSpeechRecognition(true);
        } catch (_) {}
        // Reset timer base so we don't immediately kick again.
        activeListeningStartTimeRef.current = Date.now();
        try {
          stopAudioMonitoring();
          startSpeechRecognition();
        } catch (_) {}
      }
    }, 1000);
    return () => clearInterval(interval);
    // Intentionally omit startSpeechRecognition/startAudioMonitoring from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionActive, isMicMuted, isAndroid]);

  // When truly stuck (session on, recognition not running, not muted/talking/thinking) show popup
  useEffect(() => {
    const inActiveState = isActiveVoiceState({
      sessionActive,
      isMicMuted,
      isTalking,
      isThinking,
      isListening,
      isActivelyListening, // background wake-word mode counts as listening
    });
    if (!inActiveState) {
      setVoicePermissionHelpType('both');
      if (isMobile) {
        setVoicePermissionHelpVisible(false); // Mobile can auto-dismiss when voice state recovers
      }
      return;
    }
    // On Android, "not actively listening" is expected — the audio-level monitor is running
    // instead of SR during silence. showVoicePermissionHelp() also checks audioMonitorRef.current
    // but this avoids scheduling the timeout altogether.
    if (isAndroid && audioMonitorRef.current) return;
    const t = setTimeout(() => {
      showVoicePermissionHelp('both');
    }, 8000); // longer timeout – only fire if genuinely stuck
    return () => clearTimeout(t);
  }, [sessionActive, isMicMuted, isTalking, isThinking, isListening, isActivelyListening, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    chatMessagesRef.current = chatMessages;
  }, [chatMessages]);


  // (Removed) Do not auto-show onboarding on every "Start session" screen.

  // Load avatar/voice config from server .env
  useEffect(() => {
    fetch('/api/config')
      .then(async (r) => {
        const text = await r.text();
        if (!r.ok) {
          console.error('[Avatar] /api/config failed:', r.status, r.statusText);
          return {};
        }
        if (text.trim().toLowerCase().startsWith('<!')) {
          console.warn('[Avatar] /api/config returned HTML – backend may not be running. Local: use "npm run dev".');
          return {};
        }
        try {
          return JSON.parse(text);
        } catch (_) {
          return {};
        }
      })
      .then((data) => {
        setAvatarID(data.avatarId || DEFAULT_AVATAR_ID);
        setVoiceID(data.voiceId || DEFAULT_VOICE_ID);
      })
      .catch((e) => console.error('[Avatar] /api/config fetch error:', e?.message, e));
  }, []);

  // Catch "Session not found" from LiveAvatar SDK (rejected promises or thrown errors)
  useEffect(() => {
    const handleSessionNotFoundState = () => {
      setErrorMessage('Session expired or invalid. Please click Stop then Start again.');
      setIsConnected(false);
      isConnectedRef.current = false;
      setSessionActive(false);
    };
    const onSessionNotFound = () => handleSessionNotFoundState();
    const onRejection = (event) => {
      const msg = (event?.reason?.message || String(event?.reason || '')).toLowerCase();
      if (msg.includes('session not found')) {
        event.preventDefault();
        event.stopPropagation?.();
        handleSessionNotFoundState();
      }
    };
    const onError = (event) => {
      const msg = (event?.error?.message || event?.message || '').toLowerCase();
      if (msg.includes('session not found')) {
        handleSessionNotFoundState();
        if (event.preventDefault) event.preventDefault();
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();
        return true;
      }
      return false;
    };
    window.addEventListener('session-not-found', onSessionNotFound);
    window.addEventListener('unhandledrejection', onRejection);
    window.addEventListener('error', onError, true);
    return () => {
      window.removeEventListener('session-not-found', onSessionNotFound);
      window.removeEventListener('unhandledrejection', onRejection);
      window.removeEventListener('error', onError, true);
    };
  }, []);

  // Mobile only: pin layout to visual viewport so app doesn't move when keyboard opens
  useEffect(() => {
    const vv = typeof window !== 'undefined' && window.visualViewport;
    if (!vv) return;
    const update = () => {
      requestAnimationFrame(() => {
        if (window.innerWidth >= 768) {
          setVisualViewportStyle(null);
          return;
        }
        setVisualViewportStyle({
          position: 'fixed',
          left: 0,
          top: vv.offsetTop,
          width: '100%',
          height: vv.height,
          maxHeight: vv.height,
        });
      });
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  // Mobile: prevent window scroll when keyboard opens (stops header from being pushed off-screen)
  useEffect(() => {
    if (!isMobile) return;
    const handleScroll = () => { window.scrollTo(0, 0); };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Detect if user message is a goodbye/farewell (triggers auto-disconnect after response)
  // Require minimum length to avoid false positives when user starts speaking
  const isGoodbyeMessage = (text) => {
    if (!text || typeof text !== 'string') return false;
    const t = text.trim().toLowerCase();
    if (t.length < 2) return false; // Avoid matching "I", "So", etc.
    const farewells = ['bye', 'goodbye', 'good bye', 'see you', 'see ya', 'take care', "i'm done", "im done", "that's all", 'thats all', 'farewell', 'later', 'gotta go', 'have to go'];
    // Require message to be primarily a farewell (exact match or ends with farewell)
    return farewells.some(f => t === f || t === f + '.' || t.endsWith(' ' + f) || t.endsWith(' ' + f + '.'));
  };

  const normalizeUserText = (text) => String(text || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const compactUserText = (text) => normalizeUserText(text)
    // Remove wake-style prefixes so "hey Mr. Edward Berwind ..." and plain question dedupe together.
    .replace(/^(hey|hi|hello|ok|okay)\s*,?\s*(mr\.?\s*|mister\s*)?(berwind|berwin|bervind)\s*(of\s+the\s+elms)?\s*[,\-:]*\s*/i, '')
    // Remove punctuation noise and collapse spaces.
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const isNearDuplicateText = (incomingCompact, previousCompact) => {
    if (!incomingCompact || !previousCompact) return false;
    if (incomingCompact === previousCompact) return true;
    if (incomingCompact.length >= 12 && previousCompact.length >= 12) {
      if (incomingCompact.startsWith(previousCompact) || previousCompact.startsWith(incomingCompact)) return true;
      if (incomingCompact.includes(previousCompact) || previousCompact.includes(incomingCompact)) return true;
    }
    const incomingTokens = incomingCompact.split(' ').filter(Boolean);
    const previousTokens = previousCompact.split(' ').filter(Boolean);
    if (incomingTokens.length < 2 || previousTokens.length < 2) return false;
    const tokenSet = new Set(previousTokens);
    const overlap = incomingTokens.filter((t) => tokenSet.has(t)).length;
    const ratio = overlap / Math.max(incomingTokens.length, previousTokens.length);
    return ratio >= 0.85;
  };
  const logDuplicateSkip = (reason, normalizedText, elapsedMs) => {
    if (process.env.NODE_ENV === 'production') return;
    const preview = String(normalizedText || '').slice(0, 140);
    console.debug(`[Avatar dedupe] skipped submit (${reason}) elapsed=${elapsedMs}ms text="${preview}"`);
  };
  const shouldSkipDuplicateSubmit = (text) => {
    const normalized = normalizeUserText(text);
    const compact = compactUserText(text);
    if (!normalized) return true;
    const now = Date.now();
    const elapsed = now - lastSubmittedAtRef.current;
    // Guard against delayed duplicate finals/interims from SR that can arrive long
    // after the first submit while the assistant is already answering/speaking.
    const isExactRepeat =
      normalized === lastSubmittedUserTextRef.current && elapsed < 30000;
    const isRapidDuplicate =
      normalized === lastSubmittedUserTextRef.current && elapsed < 2200;
    // Stronger window for SR interim/final variants that arrive slightly later or with tiny text drift.
    const isNearDuplicate =
      elapsed < 8000 && isNearDuplicateText(compact, lastSubmittedCompactTextRef.current);
    if (isResponseInFlightRef.current) {
      logDuplicateSkip('response-in-flight', normalized, elapsed);
      return true;
    }
    if (isRapidDuplicate) {
      logDuplicateSkip('rapid-exact-duplicate', normalized, elapsed);
      return true;
    }
    if (isNearDuplicate) {
      logDuplicateSkip('near-duplicate', normalized, elapsed);
      return true;
    }
    if (isExactRepeat) {
      logDuplicateSkip('exact-repeat-window', normalized, elapsed);
      return true;
    }
    lastSubmittedUserTextRef.current = normalized;
    lastSubmittedCompactTextRef.current = compact || normalized;
    lastSubmittedAtRef.current = now;
    return false;
  };

  // Add chat message (unique id so React always renders user + assistant bubbles)
  const addChatMessage = (text, sender) => {
    if (!text || !text.trim()) return;
    messageIdRef.current += 1;
    const isUserMessage = sender === 'user';
    const message = {
      id: `msg-${messageIdRef.current}-${Date.now()}`,
      content: isUserMessage ? String(text).trim() : toSentenceCase(text),
      role: isUserMessage ? 'user' : 'assistant',
      timestamp: new Date().toLocaleTimeString()
    };
    setChatMessages(prev => [...prev, message]);
  };

  // Speak opening greeting once we are BOTH connected + stream-ready
  const maybeSpeakOpeningGreeting = async () => {
    if (hasSpokenGreetingRef.current) return;
    if (!isConnectedRef.current || !isStreamReadyRef.current) return;
    if (!avatarInstanceRef.current) return;

    hasSpokenGreetingRef.current = true;
    showListeningAfterGreetingRef.current = true;
    addChatMessage(OPENING_GREETING, 'avatar');
    await makeAvatarSpeak(OPENING_GREETING);
  };

  // Get access token from backend
  // When ElevenLabs is configured, use CUSTOM mode so we can send our audio via repeatAudio (lip-sync from our TTS)
  const getAccessToken = async () => {
    const requestToken = async (avatar, voice, mode = 'FULL') => {
      const response = await fetch('/api/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          avatar_id: avatar,
          voice_id: voice,
          mode,
          language: 'en',
        }),
      });

      const contentType = response.headers.get('content-type') || '';
      const text = await response.text();
      if (contentType.includes('text/html') || text.trim().toLowerCase().startsWith('<!')) {
        throw new Error('BACKEND_NOT_AVAILABLE');
      }
      const data = text ? JSON.parse(text) : {};

      if (!response.ok) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }
      if (!data.token) {
        throw new Error('Token not received from server');
      }

      // Store session_id and token if available (needed for API calls like /api/task)
      if (data.session_id) {
        sessionIdRef.current = data.session_id;
        log('✅ Session ID stored:', data.session_id);
      }
      if (data.token) {
        sessionTokenRef.current = data.token;
        log('✅ Session token stored');
      }

      return { token: data.token, sessionMode: mode };
    };

    // Resolve env-first: fetch config so we always use AVATAR_ID / VOICE_ID from .env when available
    let envAvatarId = avatarID;
    let envVoiceId = voiceID;
    try {
      const configRes = await fetch('/api/config');
      const configText = await configRes.text();
      if (configRes.ok && !configText.trim().toLowerCase().startsWith('<!')) {
        try {
          const config = JSON.parse(configText);
          if (config.avatarId) envAvatarId = config.avatarId;
          if (config.voiceId) envVoiceId = config.voiceId;
          setAvatarID(envAvatarId);
          setVoiceID(envVoiceId);
        } catch (_) {}
      }
    } catch (e) {
      console.error('[Avatar] Config fetch error:', e?.message, e);
    }

    const sessionMode = 'FULL';
    console.warn('[Avatar] Requesting token:', { sessionMode });
    try {
      // First attempt: always use env (config) ids; only use default if env didn't provide them
      const preferredAvatar = envAvatarId || DEFAULT_AVATAR_ID;
      const preferredVoice = envVoiceId || DEFAULT_VOICE_ID;
      const result = await requestToken(preferredAvatar, preferredVoice, sessionMode);
      console.warn('[Avatar] Token received:', { sessionMode: result?.sessionMode });
      return result;
    } catch (primaryError) {
      console.error('[Avatar] Token fetch error, falling back to FULL:', primaryError?.message, primaryError);
      try {
        setAvatarID(DEFAULT_AVATAR_ID);
        setVoiceID(DEFAULT_VOICE_ID);
        return await requestToken(DEFAULT_AVATAR_ID, DEFAULT_VOICE_ID, 'FULL');
      } catch (fallbackError) {
        console.error('[Avatar] Token fetch failed (default avatar/voice):', fallbackError?.message, fallbackError);
        throw fallbackError;
      }
    }
  };

  // One‑time mic permission warmup (triggers browser mic prompt via getUserMedia)
  const ensureMicPermission = async () => {
    if (micWarmupDoneRef.current) return;
    if (!navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      micWarmupDoneRef.current = true;
      lastMicGrantedAtRef.current = Date.now();
      micPermissionStateRef.current = 'granted';
      setIsMicPermissionDenied(false);
    } catch (err) {
      console.error('Mic permission warmup error:', err);
      const isDenied = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError' || err?.message?.toLowerCase?.().includes('permission');
      micPermissionStateRef.current = isDenied ? 'denied' : micPermissionStateRef.current;
      if (isDenied) setIsMicPermissionDenied(true);
      const hasNoMic = err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError';
      if (hasNoMic) setHasMicDevice(false);
    }
  };

  // ── Android mic keep-alive — AudioContext approach ──────────────────────────
  // DIFFERENT APPROACH: Instead of just holding a raw getUserMedia stream, we
  // route the mic through an AudioContext (getUserMedia → createMediaStreamSource
  // → createScriptProcessor → destination).  Keeping an active AudioContext
  // ── Android mic keep-alive ───────────────────────────────────────────────────
  // The VAD library suppressed mic-on/off sounds by maintaining a COMPLETE audio
  // pipeline (mic → AudioWorklet → destination).  Android's audio subsystem sees
  // a live output session and does not fire new audio-focus open/close events when
  // SpeechRecognition starts or stops — so the OS mic sounds never play.
  //
  // Our previous approach stopped at the AnalyserNode (no destination connection).
  // Android did NOT consider that an active session, so it still played sounds on
  // each SR cycle.
  //
  // Fix: route the mic through analyser → silentGain (gain=0) → destination.
  // gain=0 means absolutely no audio reaches the speaker (no echo/feedback risk),
  // but the audio graph is "complete" and Android treats it as active media output.
  const startMicKeepAlive = async () => {
    if (!isAndroid || !ANDROID_MIC_KEEPALIVE_ENABLED) return;
    if (micKeepAliveRef.current) return;
    if (!navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      micWarmupDoneRef.current = true;
      lastMicGrantedAtRef.current = Date.now();
      micPermissionStateRef.current = 'granted';
      setIsMicPermissionDenied(false);

      // eslint-disable-next-line no-undef
      const AudioCtx = window.AudioContext || window['webkitAudioContext'];
      if (AudioCtx) {
        try {
          const ctx = new AudioCtx();
          const src = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 32;
          // Silent gain: gain=0 → no audio to speaker; but the complete graph
          // (source→analyser→gain→destination) keeps Android's audio session active.
          const silentGain = ctx.createGain();
          silentGain.gain.value = 0;
          src.connect(analyser);
          analyser.connect(silentGain);
          silentGain.connect(ctx.destination);
          ctx.resume().catch(() => {});
          micKeepAliveRef.current = { stream, ctx, src, analyser, silentGain };
          log('Mic keep-alive started (full pipeline → destination), ctx state:', ctx.state);
        } catch (ctxErr) {
          warn('Mic keep-alive AudioContext failed, stream-only fallback:', ctxErr?.message);
          micKeepAliveRef.current = stream;
        }
      } else {
        micKeepAliveRef.current = stream;
      }
    } catch (err) {
      warn('Mic keep-alive failed:', err?.message);
      const isDenied = err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';
      if (isDenied) {
        micPermissionStateRef.current = 'denied';
        setIsMicPermissionDenied(true);
      }
      const hasNoMic = err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError';
      if (hasNoMic) setHasMicDevice(false);
    }
  };

  const ensureAndroidKeepAliveHealthy = () => {
    if (!isAndroid) return;
    const ka = micKeepAliveRef.current;
    const ctx = ka?.ctx;
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    const stream = ka?.stream || (ka?.getTracks ? ka : null);
    if (stream?.getAudioTracks && stream.getAudioTracks().length === 0) {
      micKeepAliveRef.current = null;
    }
  };

  // Keep Android mic capture open continuously for the full session.
  // If the keep-alive stream/context drops, rebuild it in the background.
  useEffect(() => {
    if (!isAndroid || !sessionActive || !ANDROID_MIC_KEEPALIVE_ENABLED) return;
    let cancelled = false;
    const ensureContinuous = () => {
      if (cancelled) return;
      if (!sessionActiveRef.current) return;
      ensureAndroidKeepAliveHealthy();
      if (!micKeepAliveRef.current) {
        startMicKeepAlive().catch(() => {});
      }
    };
    // Prime immediately, then watchdog every few seconds.
    ensureContinuous();
    const interval = setInterval(ensureContinuous, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // startMicKeepAlive/ensureAndroidKeepAliveHealthy intentionally omitted (ref-based, stable enough)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAndroid, sessionActive, ANDROID_MIC_KEEPALIVE_ENABLED]);

  const stopMicKeepAlive = () => {
    stopAudioMonitoring(); // always cancel audio poll before releasing the analyser
    const ref = micKeepAliveRef.current;
    if (!ref) return;
    try {
      const stream = ref.stream || (ref.getTracks ? ref : null);
      if (stream?.getTracks) stream.getTracks().forEach(t => t.stop());
      if (ref.src)         try { ref.src.disconnect();         } catch (_) {}
      if (ref.analyser)    try { ref.analyser.disconnect();    } catch (_) {}
      if (ref.silentGain)  try { ref.silentGain.disconnect();  } catch (_) {}
      if (ref.ctx)         try { ref.ctx.close();              } catch (_) {}
    } catch (_) {}
    micKeepAliveRef.current = null;
    log('Mic keep-alive stopped');
  };

  // ── Audio-level monitor — gates SR restart on Android after first wake word ──
  // Problem: SpeechRecognition restarts every ~5 s on Android (no-speech timeout),
  // and each restart plays OS mic-enable / mic-disable sounds even with the keep-alive
  // stream.  After the user says the wake phrase once we no longer need SR to run
  // continuously — we can instead watch the AnalyserNode in the keep-alive pipeline
  // and only start a new SR session when the user actually starts speaking.
  // During silence SR is NOT running → no OS sounds.
  const stopAudioMonitoring = () => {
    if (audioMonitorRef.current) {
      cancelAnimationFrame(audioMonitorRef.current);
      audioMonitorRef.current = null;
    }
  };

  const startAudioMonitoring = () => {
    if (!isAndroid) return;
    if (!ANDROID_USE_AUDIO_MONITOR) return;
    if (audioMonitorRef.current) return; // already running
    ensureAndroidKeepAliveHealthy();
    // Moto devices sometimes fail to produce meaningful analyser values, causing the gate
    // to never start SpeechRecognition. Use direct SR for reliability on Moto.
    if (isMotoAndroid) {
      startSpeechRecognition();
      return;
    }

    let ka = micKeepAliveRef.current;

    // No keep-alive stream at all — fall through to direct SR so the app still works.
    if (!ka) {
      log('Audio monitor: no keep-alive, using SR immediately and recovering keep-alive in background');
      // Critical for reliability on some Android devices (e.g. Moto): start SR now.
      // Do not wait for keep-alive recovery promise before listening.
      startSpeechRecognition();
      if (ANDROID_MIC_KEEPALIVE_ENABLED) {
        startMicKeepAlive().then(() => {
          if (!sessionActiveRef.current || isMicMutedRef.current) return;
          // Keep-alive recovered; future SR restarts can use monitor path.
        }).catch(() => {});
      }
      return;
    }

    // Keep-alive is a raw MediaStream (AudioContext creation failed in startMicKeepAlive).
    // Try to build the AudioContext now — we may be inside a user-gesture context still.
    if (!ka.analyser) {
      try {
        // eslint-disable-next-line no-undef
        const AudioCtx = window.AudioContext || window['webkitAudioContext'];
        if (AudioCtx) {
          const stream = ka.stream || (ka.getTracks ? ka : null);
          if (stream) {
            const ctx = new AudioCtx();
            const src = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 32;
            const silentGain = ctx.createGain();
            silentGain.gain.value = 0;
            src.connect(analyser);
            analyser.connect(silentGain);
            silentGain.connect(ctx.destination);
            ctx.resume().catch(() => {});
            micKeepAliveRef.current = { stream, ctx, src, analyser, silentGain };
            ka = micKeepAliveRef.current;
            log('Audio monitor: built AudioContext from existing stream');
          }
        }
      } catch (err) {
        warn('Audio monitor: AudioContext rebuild failed:', err?.message);
      }
      // Still no analyser after rebuild attempt → fall back to direct SR
      if (!ka.analyser) {
        log('Audio monitor: no analyser available, falling back to SR');
        startSpeechRecognition();
        return;
      }
    }

    // Ensure AudioContext is running before we start polling
    if (ka.ctx && ka.ctx.state === 'suspended') {
      ka.ctx.resume().catch(() => {});
    }

    const analyser = ka.analyser;
    const buf = new Uint8Array(analyser.frequencyBinCount);
    let activeFrms = 0;
    let zeroFrms   = 0;
    let pollFrames = 0;
    const THRESHOLD      = 18;  // 0–255; speech typically 30–100, silence < 10–15
    const NEEDED         = 3;   // ~50 ms at 60 fps
    const ZERO_RESUME_AT = 90;  // ~1.5 s of all-zero → try AudioContext resume
    const ZERO_BAIL_AT   = 300; // ~5 s of all-zero → context is stuck, fall back to SR

    const poll = () => {
      if (!sessionActiveRef.current || isMicMutedRef.current) {
        audioMonitorRef.current = null;
        return;
      }
      pollFrames += 1;
      // If the gate never opens (analyser stuck between 1–THRESHOLD), still allow SR after ~9s.
      if (pollFrames > 540) {
        audioMonitorRef.current = null;
        warn('Audio monitor: timeout, falling back to direct SpeechRecognition');
        startSpeechRecognition();
        return;
      }
      analyser.getByteFrequencyData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i];
      const avg = sum / buf.length;

      // While the avatar is speaking its voice reaches the mic and would falsely
      // trigger SR every ~5 s (the no-speech timeout cycle).  Reset the counters
      // so we only start SR when the USER is the one making noise.
      if (isAvatarTalkingRef.current) {
        activeFrms = 0;
        zeroFrms   = 0;
        audioMonitorRef.current = requestAnimationFrame(poll);
        return;
      }

      if (avg > THRESHOLD) {
        activeFrms++;
        zeroFrms = 0;
        if (activeFrms >= NEEDED) {
          activeFrms = 0;
          audioMonitorRef.current = null; // stop poll — SR will take over
          startSpeechRecognition();
          return;
        }
      } else {
        activeFrms = 0;
        if (avg === 0) {
          zeroFrms++;
          // AudioContext may have been suspended by the OS — nudge it to resume
          if (zeroFrms === ZERO_RESUME_AT) {
            const ctx = micKeepAliveRef.current?.ctx;
            if (ctx && ctx.state !== 'closed') ctx.resume().catch(() => {});
          }
          // After 5 s of all-zeros the AudioContext is genuinely stuck — fall back to SR
          if (zeroFrms >= ZERO_BAIL_AT) {
            audioMonitorRef.current = null;
            warn('Audio monitor: context stuck (all zeros), falling back to SR cycling');
            startSpeechRecognition();
            return;
          }
        } else {
          zeroFrms = 0;
        }
      }
      audioMonitorRef.current = requestAnimationFrame(poll);
    };
    audioMonitorRef.current = requestAnimationFrame(poll);
    log('Audio monitor started');
  };

  // Re-assert avatar playback state to recover occasional mobile audio ducking/muffling.
  // This does not alter interruption flow; it only reinforces output gain and playback.
  const reinforceAvatarAudioPlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    try {
      video.muted = false;
      if (typeof video.volume === 'number') video.volume = AVATAR_OUTPUT_VOLUME;
      const playPromise = video.play?.();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    } catch (_) {}
    if (avatarAudioRecoveryTimeoutRef.current) {
      clearTimeout(avatarAudioRecoveryTimeoutRef.current);
    }
    avatarAudioRecoveryTimeoutRef.current = setTimeout(() => {
      const retryVideo = videoRef.current;
      if (!retryVideo) return;
      try {
        retryVideo.muted = false;
        if (typeof retryVideo.volume === 'number') retryVideo.volume = AVATAR_OUTPUT_VOLUME;
        const retryPromise = retryVideo.play?.();
        if (retryPromise && typeof retryPromise.catch === 'function') {
          retryPromise.catch(() => {});
        }
      } catch (_) {}
    }, 140);
  };

  // Android: do not start SpeechRecognition until the microphone is actually granted and capture
  // is ready (keep-alive pipeline if enabled, else one-shot warmup like iOS).
  const isAndroidMicReadyForSr = () => {
    if (!isAndroid) return true;
    if (micPermissionStateRef.current !== 'granted') return false;
    if (ANDROID_MIC_KEEPALIVE_ENABLED) return !!micKeepAliveRef.current;
    return micWarmupDoneRef.current;
  };

  const clearSessionIdleTimer = useCallback(() => {
    if (sessionIdleTimerRef.current) {
      clearTimeout(sessionIdleTimerRef.current);
      sessionIdleTimerRef.current = null;
    }
  }, []);

  const isSessionBusyForIdle = useCallback(() => (
    isAvatarTalkingRef.current ||
    isResponseInFlightRef.current ||
    isThinkingRef.current ||
    isUserSpeakingRef.current
  ), []);

  const scheduleSessionIdleTimeout = useCallback(() => {
    clearSessionIdleTimer();
    if (!sessionActiveRef.current || isClosingRef.current) return;

    sessionIdleTimerRef.current = setTimeout(() => {
      sessionIdleTimerRef.current = null;
      if (!sessionActiveRef.current || isClosingRef.current) return;

      if (isSessionBusyForIdle()) {
        lastSessionActivityRef.current = Date.now();
        scheduleSessionIdleTimeout();
        return;
      }

      const idleMs = Date.now() - lastSessionActivityRef.current;
      if (idleMs >= SESSION_IDLE_MS) {
        log('Session ended after 3 minutes of inactivity');
        pushDataLayerEvent('session_idle_timeout', {
          page: 'avatar',
          idle_ms: String(idleMs),
        });
        handleCloseRef.current?.();
        return;
      }

      scheduleSessionIdleTimeout();
    }, SESSION_IDLE_MS);
  }, [clearSessionIdleTimer, isSessionBusyForIdle]);

  const touchSessionActivity = useCallback(() => {
    lastSessionActivityRef.current = Date.now();
    if (sessionActiveRef.current && !isClosingRef.current) {
      scheduleSessionIdleTimeout();
    }
  }, [scheduleSessionIdleTimeout]);

  touchSessionActivityRef.current = touchSessionActivity;

  // Start session
  const handleStart = async () => {
    if (isConnecting || isConnected) return;
    trackButtonClick('start_session', { page: 'avatar' });
    pushDataLayerEvent('session_start_attempt', { page: 'avatar' });
    // Ensure mic starts unmuted for each new session
    isMicMutedRef.current = false;
    micGateRef.current = false;
    manualMuteRef.current = false;
    autoMutedForSpeechRef.current = false;
    setIsMicMuted(false);

    setIsConnecting(true);
    setIsEndingSession(false);
    setErrorType(null);
    setErrorMessage(null);

    try {
      // Use this user gesture to pre-unlock video audio where allowed (especially for iOS/WebView)
      if (videoRef.current) {
        try {
          videoRef.current.muted = false;
          if (typeof videoRef.current.volume === 'number') videoRef.current.volume = AVATAR_OUTPUT_VOLUME;
          const playPromise = videoRef.current.play();
          if (playPromise && typeof playPromise.then === 'function') {
            playPromise.catch(() => {});
          }
        } catch (e) {
          // Safe to ignore here; stream may not be attached yet
        }
      }

      if (isAndroid) {
        // Default: one mic path (warmup only) — best for Web Speech. Opt-in keep-alive via env.
        if (ANDROID_MIC_KEEPALIVE_ENABLED) {
          await startMicKeepAlive();
        } else {
          await ensureMicPermission();
        }
      } else {
        await ensureMicPermission();
      }

      // Skip mic warm-up: requesting then immediately releasing causes enable/disable sounds.
      // Permission will be requested when stream is ready and we actually need the mic.

      // Reset per-session flags
      isStreamReadyRef.current = false;
      isConnectedRef.current = false;
      hasSpokenGreetingRef.current = false;
      showListeningAfterGreetingRef.current = false;
      wakeActiveRef.current = false;
      sessionWakeActivatedRef.current = false;
      recentSpeechRef.current = '';
      {
        const wc = wakeCoalesceRef.current;
        if (wc.timer) clearTimeout(wc.timer);
        wc.timer = null;
        wc.transcripts = [];
      }
      hasSavedThisSessionRef.current = false;
      hasReceivedAnySpeechRef.current = false;
      activeListeningStartTimeRef.current = null;
      setVoicePermissionHelpType('both');
      setVoicePermissionHelpVisible(false);
      // Sync mic permission state from Permissions API when supported (Chrome, etc.) so popup shows correct message
      if (typeof navigator?.permissions?.query === 'function') {
        navigator.permissions.query({ name: 'microphone' }).then((status) => {
          if (status?.state === 'granted') {
            micPermissionStateRef.current = 'granted';
            setIsMicPermissionDenied(false);
          } else if (status?.state === 'denied') {
            micPermissionStateRef.current = 'denied';
            setIsMicPermissionDenied(true);
          }
        }).catch(() => {});
      }

      // Clean up existing session
      if (avatarInstanceRef.current) {
        try {
          await avatarInstanceRef.current.stop();
        } catch (e) {
          // Ignore cleanup errors
        }
      }

      // Get token
      const tokenResult = await getAccessToken();
      const token = typeof tokenResult === 'object' && tokenResult !== null && tokenResult.token
        ? tokenResult.token
        : tokenResult;
      // Create session
      // Start with voiceChat: false - microphone will only be enabled when user clicks mic button
      const session = new LiveAvatarSession(token, {
        voiceChat: false, // Disable microphone initially - only enable when user clicks mic button
        apiUrl: API_URL
      });
      
      // session_id was already stored in sessionIdRef by getAccessToken(); use it for this session
      if (sessionIdRef.current) {
        session._sessionId = sessionIdRef.current;
        log('✅ Session ID stored:', sessionIdRef.current);
      }
      log('Session created:', {
        hasMessage: typeof session.message === 'function',
        hasRepeat: typeof session.repeat === 'function',
        sessionState: session.state,
        sessionInfo: session._sessionInfo
      });

      // Session state handler
      session.on(SessionEvent.SESSION_STATE_CHANGED, (state) => {
        pushDataLayerEvent('session_state_changed', { page: 'avatar', session_state: String(state || 'unknown') });
        if (state === SessionState.CONNECTED) {
          pushDataLayerEvent('session_connected', { page: 'avatar' });
          setIsConnected(true);
          isConnectedRef.current = true;
          setMode('live');
          // Greeting may fire here if stream is already ready
          maybeSpeakOpeningGreeting().catch((err) => {
            if (!handleSessionNotFound(err)) console.error('[Avatar] Greeting failed:', err?.message || err);
          });
        } else if (state === SessionState.DISCONNECTED) {
          setSessionActive(false);
          setIsConnected(false);
          isConnectedRef.current = false;
          setIsTalking(false);
        }
      });

      // Stream ready handler – attach video as soon as stream is ready; video must be visible and have size
      session.on(SessionEvent.SESSION_STREAM_READY, () => {
        pushDataLayerEvent('session_stream_ready', { page: 'avatar' });
        setSessionActive(true);
        // Also update the ref immediately so auto speech recognition
        // doesn't early‑return on a stale sessionActiveRef value.
        sessionActiveRef.current = true;
        sessionStartTimeRef.current = new Date().toISOString();
        hasSavedThisSessionRef.current = false;
        isStreamReadyRef.current = true;
        setVideoReady(false); // will flip to true once attach/play succeeds
        log('Session stream ready - avatar can now speak');

        const doAttach = () => {
          if (videoRef.current) {
            try {
              session.attach(videoRef.current);
              // Stream is ready – ensure video is unmuted so user hears avatar
              videoRef.current.muted = false;
              if (typeof videoRef.current.volume === 'number') videoRef.current.volume = AVATAR_OUTPUT_VOLUME;
              if (videoRef.current.paused) {
                videoRef.current.play().then(() => {
                  setVideoReady(true);
                }).catch(() => {});
              } else {
                setVideoReady(true);
              }
              log('Avatar video attached');
            } catch (error) {
              console.error('Video attachment error:', error);
            }
          }
        };
        doAttach();
        setTimeout(doAttach, 150);
        // Greeting may fire here if we're already connected
        setTimeout(() => {
          maybeSpeakOpeningGreeting().catch((err) => {
            if (!handleSessionNotFound(err)) console.error('[Avatar] Greeting failed:', err?.message || err);
          });
        }, 250);

        // Speech recognition: start when stream is ready. Android waits until mic is actually
        // enabled (permission + keep-alive or warmup) so SR does not run before capture exists.
        try {
          const runSr = () => {
            if (isAndroid) {
              wakeActiveRef.current = true;
              sessionWakeActivatedRef.current = true;
              log('Android: speech recognition starting with stream (no wake word required)');
              androidSrDebugLog('STREAM_READY → startSR (mic ready, wake primed)');
            }
            (androidShouldGateSrWithAudioMonitor() ? startAudioMonitoring() : startSpeechRecognition());
          };

          if (isAndroid) {
            if (isAndroidMicReadyForSr()) {
              runSr();
            } else {
              androidSrDebugLog('STREAM_READY: defer SR until mic enabled');
              let attempts = 0;
              const iv = setInterval(() => {
                attempts += 1;
                if (!sessionActiveRef.current) {
                  clearInterval(iv);
                  return;
                }
                if (isAndroidMicReadyForSr()) {
                  clearInterval(iv);
                  runSr();
                  return;
                }
                if (attempts >= 50) {
                  clearInterval(iv);
                  if (micPermissionStateRef.current === 'denied') {
                    androidSrDebugLog('SR not started: microphone permission denied');
                    showVoicePermissionHelp('microphone');
                  } else {
                    androidSrDebugLog('SR start after mic wait timeout (permission not denied)');
                    wakeActiveRef.current = true;
                    sessionWakeActivatedRef.current = true;
                    (androidShouldGateSrWithAudioMonitor() ? startAudioMonitoring() : startSpeechRecognition());
                  }
                }
              }, 200);
            }
          } else {
            (androidShouldGateSrWithAudioMonitor() ? startAudioMonitoring() : startSpeechRecognition());
          }
        } catch (e) {
          androidSrDebugLog(`STREAM_READY startSR failed: ${e?.message || e}`);
          console.error('Auto startSpeechRecognition failed:', e?.message || e);
        }
      });

      // Disconnect handler
      session.on(SessionEvent.SESSION_DISCONNECTED, () => {
        pushDataLayerEvent('session_disconnected', { page: 'avatar' });
        androidSrDebugLog('SESSION_DISCONNECTED');
        // Mark session inactive and release microphone access when session disconnects
        sessionActiveRef.current = false;
        stopSpeechRecognition();
        setSessionActive(false);
        setIsTalking(false);
        setIsConnected(false);
        if (speakWatchdogRef.current) {
          clearTimeout(speakWatchdogRef.current);
          speakWatchdogRef.current = null;
        }
      });

      // Avatar speaking handlers
      session.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, () => {
        pushDataLayerEvent('avatar_speak_started', { page: 'avatar' });
        setIsTalking(true);
        setIsThinking(false); // Keep Thinking label until avatar actually starts speaking
        isAvatarTalkingRef.current = true;
        log('Avatar started speaking');
        reinforceAvatarAudioPlayback();
        if (speakWatchdogRef.current) {
          clearTimeout(speakWatchdogRef.current);
          speakWatchdogRef.current = null;
        }
        setIsListening(false);
        recentSpeechRef.current = ''; // prevent avatar speech echo from triggering wake word
        // Auto-mute while the avatar speaks. This is a SILENT mute: we close the
        // software gate so recognition results (including the avatar's own audio
        // echoing back through the mic) are discarded. We deliberately do NOT stop
        // recognition — stopping/restarting SR is what makes phones play the OS mic
        // beep on every mute/unmute. The user can tap Unmute to barge in and interrupt.
        if (AUTO_MUTE_WHILE_SPEAKING && !manualMuteRef.current && sessionActiveRef.current) {
          autoMutedForSpeechRef.current = true;
          micGateRef.current = true;
          isMicMutedRef.current = true;
          setIsMicMuted(true);
          androidSrDebugLog('AVATAR_SPEAK_STARTED — silent auto-mute (gate closed, SR keeps running)');
          // Drop any half-captured speech so it isn't submitted later.
          const coalesce = wakeCoalesceRef.current;
          if (coalesce?.timer) clearTimeout(coalesce.timer);
          if (coalesce) { coalesce.timer = null; coalesce.transcripts = []; }
        }
      });

      session.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => {
        pushDataLayerEvent('avatar_speak_ended', { page: 'avatar' });
        setIsTalking(false);
        isAvatarTalkingRef.current = false;
        if (speakWatchdogRef.current) {
          clearTimeout(speakWatchdogRef.current);
          speakWatchdogRef.current = null;
        }
        log('Avatar finished speaking');
        touchSessionActivityRef.current();
        androidSrDebugLog('AVATAR_SPEAK_ENDED → restart SR soon');
        // Silently re-open the gate if we auto-muted for the avatar's speech. If the
        // user manually muted meanwhile, leave it muted (their choice wins). No SR
        // stop/restart here, so no mic beep — we just start accepting results again.
        if (autoMutedForSpeechRef.current && !manualMuteRef.current) {
          autoMutedForSpeechRef.current = false;
          micGateRef.current = false;
          isMicMutedRef.current = false;
          setIsMicMuted(false);
        }
        // Android: keep conversation mode on (same as iOS after greeting).
        if (isAndroid && sessionActiveRef.current && !isMicMutedRef.current) {
          wakeActiveRef.current = true;
          sessionWakeActivatedRef.current = true;
        }
        if (showListeningAfterGreetingRef.current && sessionActiveRef.current && !isMicMutedRef.current) {
          setIsListening(true);
          // After the opening greeting, allow immediate questions without requiring a wake word.
          // This also keeps the Listening label visible on Android (Moto/Samsung), where SR
          // may start in a "quiet" mode when wakeActiveRef is false.
          wakeActiveRef.current = true;
          showListeningAfterGreetingRef.current = false;
        }
        reinforceAvatarAudioPlayback();
        if (pendingGoodbyeDisconnectRef.current) {
          pendingGoodbyeDisconnectRef.current = false;
          setTimeout(() => {
            handleCloseRef.current?.();
          }, 2000);
        }
        // If the avatar just finished a wake-word greeting ("Yes? How may I help you?"),
        // reset the coalesce timer so the user gets a FULL window starting NOW
        if (wakeActiveRef.current && sessionActiveRef.current && !isMicMutedRef.current) {
          const coalesce = wakeCoalesceRef.current;
          if (coalesce.timer) clearTimeout(coalesce.timer);
          coalesce.timer = setTimeout(() => submitWakeQueryRef.current?.(), 7000);
        }
        // Restore Listening badge + restart capture
        if (sessionActiveRef.current && !isMicMutedRef.current) {
          setIsListening(Boolean(wakeActiveRef.current));
          if (isAndroid) {
            // If SR stayed up during playback (barge-in path), only refresh UI — do not stop/recycle recognition.
            setTimeout(() => {
              if (!sessionActiveRef.current || isMicMutedRef.current || isAvatarTalkingRef.current) return;
              if (isActivelyListeningRef.current) {
                setIsListening(Boolean(wakeActiveRef.current));
                androidSrDebugLog('AVATAR_SPEAK_ENDED: SR still active — skip recycle');
                return;
              }
              isActivelyListeningRef.current = false;
              setIsActivelyListening(false);
              const go = () =>
                androidShouldGateSrWithAudioMonitor() ? startAudioMonitoring() : startSpeechRecognition();
              requestAnimationFrame(() => setTimeout(go, 90));
            }, 520);
          } else if (isActivelyListeningRef.current) {
            /* desktop: SR still running */
          } else if (isLegacyIOSSafari) {
            setTimeout(() => {
              if (!sessionActiveRef.current || isMicMutedRef.current || isAvatarTalkingRef.current) return;
              startSpeechRecognition();
            }, 650);
          } else {
            startSpeechRecognition();
          }
        }
      });

      // Note: We're not using SDK's voiceChat feature (voiceChat: false)
      // Instead, we use browser's Speech Recognition API when mic button is clicked
      // So we don't need USER_TRANSCRIPTION, USER_SPEAK_STARTED, or USER_SPEAK_ENDED handlers

      // Store session reference
      avatarInstanceRef.current = session;
      
      // Start session
      await session.start();
      // Keep latest session id for fallback /api/task calls.
      sessionIdRef.current = session._sessionInfo?.session_id || session.sessionId || sessionIdRef.current;
      
      // Fallback video attachment
      setTimeout(() => {
        if (videoRef.current && session.state === SessionState.CONNECTED) {
          try {
            session.attach(videoRef.current);
          } catch (error) {
            // Will retry on stream ready
          }
        }
      }, 500);
      
    } catch (error) {
      pushDataLayerEvent('session_start_failed', {
        page: 'avatar',
        error_name: String(error?.name || 'unknown'),
        error_message: String(error?.message || 'unknown'),
      });
      console.error('Session connection failed:', error);
      if (handleSessionNotFound(error)) return;
      const msg = (error?.message || '').trim();
      const errorName = error.name || '';

      // API returned HTML (index.html) – backend not running or not deployed
      const isHtmlResponse = msg === 'BACKEND_NOT_AVAILABLE' ||
        (msg.includes('Unexpected token') && (msg.includes('<!doctype') || msg.includes('not valid JSON')));
      if (isHtmlResponse) {
        setErrorType('GENERAL');
        setErrorMessage(
          'Backend not available: /api requests are returning the app page instead of JSON. ' +
          'Local: in the project folder run "npm run dev" (starts both backend on :5000 and app on :3000). ' +
          'Do not run only "npm start" — the backend must be running. ' +
          'Production: ensure the api/ folder is deployed (e.g. Vercel serverless functions) and API URL is same origin.'
        );
        setSessionActive(false);
        setIsConnected(false);
        setIsConnecting(false);
        return;
      }

      // Check for permission errors first
      if (errorName === 'NotAllowedError' ||
          msg.includes('Permission denied') ||
          msg.includes('permission')) {
        micPermissionStateRef.current = 'denied';
        setIsMicPermissionDenied(true);
        setErrorType('AUTH');
        setErrorMessage('End session first. Then go to Settings → Privacy & Security and enable Speech Recognition and Microphone for your browser. Ensure the site is served over HTTPS. Start a new session after enabling.');
      } else if (msg.includes('UNAUTHORIZED_KEY_TYPE') || 
          msg.includes('401') || 
          msg.toLowerCase().includes('unauthorized') || 
          msg.includes('400112')) {
        setErrorType('AUTH');
        setErrorMessage("A 'Streaming' API Key is required for live video. Please check your API key configuration.");
      } else if (msg.toLowerCase().includes('avatar') && msg.toLowerCase().includes('not found')) {
        setErrorType('GENERAL');
        setErrorMessage('Avatar not found. Set AVATAR_ID and VOICE_ID in your server .env with the avatar and voice IDs from your LiveAvatar/HeyGen dashboard, then restart the server.');
      } else if (msg.toLowerCase().includes('internal server error') || msg.includes('500') || msg.includes('API request failed')) {
        setErrorType('GENERAL');
        const serverReason = msg ? `"${msg.trim()}"` : 'Server error';
        setErrorMessage(
          `LiveAvatar: ${serverReason}. This often means exhausted credits, an invalid API key, or a temporary outage. ` +
          'Check your HeyGen/LiveAvatar dashboard for API usage and credits. In Vercel: set REACT_APP_HEYGEN_API_KEY or LIVEAVATAR_API_KEY in Project → Settings → Environment Variables, then redeploy.'
        );
      } else {
        setErrorType('GENERAL');
        setErrorMessage(msg || "Connection failed. Check avatar_id, voice_id, and API key in your LiveAvatar/HeyGen dashboard.");
      }
      
      setSessionActive(false);
      setIsConnected(false);
    } finally {
      setIsConnecting(false);
    }
  };

  // Static mode handler
  const handleStaticMode = () => {
    trackButtonClick('archive_mode', { page: 'avatar' });
    pushDataLayerEvent('archive_mode_enabled', { page: 'avatar' });
    setMode('static');
    setIsConnected(true);
    setErrorType(null);
    setSessionActive(true);
  };

  // Initialize browser Speech Recognition API (tuned for desktop when applicable)
  const initializeSpeechRecognition = () => {
    // Chrome uses webkitSpeechRecognition; try webkit first for best compatibility
    const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
    if (!SpeechRecognition) {
      warn('Speech Recognition API not supported in this browser');
      return null;
    }

    const isDesktop = typeof window !== 'undefined' &&
      window.innerWidth >= 768 &&
      !('ontouchstart' in window);

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true; // Always on – keeps session alive and reduces no-speech timeouts
    recognition.maxAlternatives = 3; // More alternatives = better chance of matching wake phrase
    // Android Chrome is flaky with non–en-US tags; force en-US for reliable cloud SR.
    recognition.lang = isAndroid
      ? 'en-US'
      : ((typeof navigator !== 'undefined' && /^en(-[A-Za-z]{2})?$/i.test(navigator.language))
        ? navigator.language
        : 'en-US');

    recognition.onstart = () => {
      if (recognitionRef.current !== recognition) return;
      log('Speech recognition started');
      androidSrDebugLog('SR onstart');
      // iOS/desktop: quiet until wake. Android: wake primed at stream ready — show Listening when active.
      setIsListening(Boolean(wakeActiveRef.current));
    };

    recognition.onresult = (event) => {
      if (recognitionRef.current !== recognition) return;
      if (micGateRef.current) return; // silent mute: recognition keeps running, results discarded
      const results = event.results;
      if (!results.length) return;

      const lastResult = results[results.length - 1];

      // Thin wrapper — always delegates to the ref so the latest closures are used.
      const submitWakeQuery = () => submitWakeQueryRef.current?.();

      // Best-effort "user speaking" indicator (used only for UI labels).
      // Any non-empty transcript (interim or final) means user is actively speaking.
      try {
        const anyTxt = String(lastResult?.[0]?.transcript || '').trim();
        if (anyTxt) {
          setIsUserSpeaking(true);
          if (userSpeakingTimeoutRef.current) clearTimeout(userSpeakingTimeoutRef.current);
          userSpeakingTimeoutRef.current = setTimeout(() => {
            setIsUserSpeaking(false);
          }, 900);
        }
      } catch (_) {}

      // ── Interim (non-final) results ────────────────────────────────────────
      if (!lastResult.isFinal) {
        // Legacy iOS Safari: allow interruption using interim speech while avatar is speaking.
        if (isLegacyIOSSafari && isAvatarTalkingRef.current) {
          const interimText = String(lastResult[0]?.transcript || '').trim();
          const wordCount = interimText.split(/\s+/).filter(Boolean).length;
          if (interimText && wordCount >= 2) {
            try { stopAvatarNarration(); } catch (_) {}
            const vc = voiceAbortControllerRef.current;
            if (vc) try { vc.abort(); } catch (_) {}
            const cc = chatAbortControllerRef.current;
            if (cc) try { cc.abort(); } catch (_) {}
            wakeActiveRef.current = true;
            sessionWakeActivatedRef.current = true;
            isActivelyListeningRef.current = true;
            setIsActivelyListening(true);
            setIsListening(true);
            const coalesce = wakeCoalesceRef.current;
            coalesce.transcripts = [interimText];
            if (coalesce.timer) clearTimeout(coalesce.timer);
            coalesce.timer = setTimeout(submitWakeQuery, 1200);
            return;
          }
        }

        // Legacy iOS Safari fallback: accept longer interim phrases as a query because "final"
        // often never arrives on older devices.
        if (isLegacyIOSSafari && !wakeActiveRef.current && !isAvatarTalkingRef.current) {
          const interimText = String(lastResult[0]?.transcript || '').trim();
          const words = interimText.split(/\s+/).filter(Boolean);
          if (interimText && words.length >= 4) {
            wakeActiveRef.current = true;
            isActivelyListeningRef.current = true;
            setIsActivelyListening(true);
            setIsListening(true);
            const coalesce = wakeCoalesceRef.current;
            coalesce.transcripts = [interimText];
            if (coalesce.timer) clearTimeout(coalesce.timer);
            coalesce.timer = setTimeout(submitWakeQuery, 1800);
            return;
          }
        }

        // Android Chrome: same as legacy iOS — finals are often missing or very late; interim must carry the query.
        if (isAndroid && isAvatarTalkingRef.current) {
          const interimText = String(lastResult[0]?.transcript || '').trim();
          const wordCount = interimText.split(/\s+/).filter(Boolean).length;
          if (interimText && wordCount >= 2) {
            try { stopAvatarNarration(); } catch (_) {}
            const vc = voiceAbortControllerRef.current;
            if (vc) try { vc.abort(); } catch (_) {}
            const cc = chatAbortControllerRef.current;
            if (cc) try { cc.abort(); } catch (_) {}
            wakeActiveRef.current = true;
            sessionWakeActivatedRef.current = true;
            isActivelyListeningRef.current = true;
            setIsActivelyListening(true);
            setIsListening(true);
            const coalesce = wakeCoalesceRef.current;
            coalesce.transcripts = [interimText];
            if (coalesce.timer) clearTimeout(coalesce.timer);
            coalesce.timer = setTimeout(submitWakeQuery, 1200);
            return;
          }
        }

        // Android: long interim without finals (same fallback as legacy iOS) if wake not yet set.
        if (isAndroid && !wakeActiveRef.current && !isAvatarTalkingRef.current) {
          const interimText = String(lastResult[0]?.transcript || '').trim();
          const words = interimText.split(/\s+/).filter(Boolean);
          if (interimText && words.length >= 4) {
            wakeActiveRef.current = true;
            sessionWakeActivatedRef.current = true;
            isActivelyListeningRef.current = true;
            setIsActivelyListening(true);
            setIsListening(true);
            const coalesce = wakeCoalesceRef.current;
            coalesce.transcripts = [interimText];
            if (coalesce.timer) clearTimeout(coalesce.timer);
            coalesce.timer = setTimeout(submitWakeQuery, 1800);
            return;
          }
        }

        if (wakeActiveRef.current) {
          // Android often ends the SR session before a "final" result; only the interim
          // string exists. If we never copy interim into coalesce, submitWakeQuery fires
          // with an empty merge and the avatar ignores the user.
          const interimText = String(lastResult[0]?.transcript || '').trim();
          const coalesce = wakeCoalesceRef.current;
          if (interimText && !isAvatarTalkingRef.current) {
            const cleanedInterim = interimText.replace(/^(hey|hi|hello|ok|okay)\s*,?\s*(mr\.?\s*|mister\s*)?(berwind|berwin|bervind)\s*(of\s+the\s+elms)?[,\s]*/i, '').trim();
            const toStore = cleanedInterim || interimText;
            if (toStore) coalesce.transcripts = [toStore];
          }
          // User is still speaking — extend submit timer so it never fires mid-sentence.
          if (coalesce.timer) clearTimeout(coalesce.timer);
          coalesce.timer = setTimeout(submitWakeQuery, 1500);
          return;
        }
        // Non-Android: detect wake phrase in interim (split utterances, optional greeting).
        if (!isAndroid && !isAvatarTalkingRef.current) {
          const interimText = String(lastResult[0]?.transcript || '').trim();
          if (interimText) {
            const interimLower = interimText.toLowerCase();
            const bufPlusInterim = (recentSpeechRef.current + ' ' + interimLower).trim().slice(-300);
            const interimWake = findWakePhrase(interimLower) || findWakePhrase(bufPlusInterim);
            if (interimWake) {
              recentSpeechRef.current = '';
              wakeActiveRef.current = true;
              sessionWakeActivatedRef.current = true;
              isActivelyListeningRef.current = true;
              setIsActivelyListening(true);
              setIsListening(true);
              const after = extractAfterWake(interimText, interimWake);
              if (after) {
                const coalesce = wakeCoalesceRef.current;
                coalesce.transcripts = [after];
                if (coalesce.timer) clearTimeout(coalesce.timer);
                coalesce.timer = setTimeout(submitWakeQuery, 1500);
              } else {
                const greeting = WAKE_GREETINGS[wakeCountRef.current % WAKE_GREETINGS.length];
                wakeCountRef.current += 1;
                makeAvatarSpeak(greeting).catch(() => {});
                const coalesce = wakeCoalesceRef.current;
                if (coalesce.timer) clearTimeout(coalesce.timer);
                coalesce.timer = setTimeout(submitWakeQuery, 7000);
              }
            }
          }
        }
        return;
      }
      // ────────────────────────────────────────────────────────────────────────

      // Collect ALL alternatives (Chrome returns up to maxAlternatives).
      // Using every alternative maximises the chance of catching the wake phrase even
      // when the top-confidence transcript is wrong (e.g. "playing" vs "hey Mr. Edward Berwind").
      const allAlts = [];
      for (let j = 0; j < lastResult.length; j++) {
        const txt = String(lastResult[j].transcript || '').trim();
        if (txt) allAlts.push(txt);
      }
      if (!allAlts.length) return;

      // Primary transcript = first (highest-confidence) alternative.
      const t = allAlts[0];

      log('Speech recognized:', t, allAlts.length > 1 ? `(+${allAlts.length - 1} alts)` : '');
      androidSrDebugLog(`SR final: ${t.length > 80 ? `${t.slice(0, 80)}…` : t}`);
      lastSpeechResultTimeRef.current = Date.now();
      hasReceivedAnySpeechRef.current = true;
      setVoicePermissionHelpType('both');
      if (isMobile) {
        setVoicePermissionHelpVisible(false);
      }

      // ── Natural barge-in ────────────────────────────────────────────────────
      // While the avatar is speaking, ANY speech of ≥ 2 words interrupts it.
      // This gives a "real conversation" feel — no wake phrase needed to cut in.
      if (isAvatarTalkingRef.current) {
        const wordCount = t.split(/\s+/).filter(Boolean).length;
        if (wordCount >= 2) {
          try { stopAvatarNarration(); } catch (_) {}
          const vc = voiceAbortControllerRef.current;
          if (vc) try { vc.abort(); } catch (_) {}
          const cc = chatAbortControllerRef.current;
          if (cc) try { cc.abort(); } catch (_) {}

          // Android: no wake-word greetings — send question text only (strip optional wake prefix).
          if (isAndroid) {
            let toSend = t.replace(/^(hey|hi|hello|ok|okay)\s*,?\s*(mr\.?\s*|mister\s*)?(berwind|berwin|bervind)\s*(of\s+the\s+elms)?[,\s]*/i, '').trim();
            if (!toSend) {
              for (const alt of allAlts) {
                const w = findWakePhrase(alt);
                if (w) {
                  const after = extractAfterWake(alt, w);
                  if (after) { toSend = after; break; }
                }
              }
            }
            if (!toSend) toSend = t;
            wakeActiveRef.current = true;
            sessionWakeActivatedRef.current = true;
            const coalesce = wakeCoalesceRef.current;
            if (coalesce.timer) clearTimeout(coalesce.timer);
            coalesce.timer = null;
            coalesce.transcripts = [];
            recentSpeechRef.current = '';
            handleVoiceInput(toSend, { appendUserMessage: true }).catch((err) => console.error('Barge-in error:', err));
            return;
          }

          // If they also said the wake phrase (e.g. "hey Mr. Edward Berwind what about X"), handle gracefully.
          let bargeWake = null, bargeWakeSource = '';
          for (const alt of allAlts) {
            const w = findWakePhrase(alt);
            if (w) { bargeWake = w; bargeWakeSource = alt; break; }
          }

          if (bargeWake) {
            const after = extractAfterWake(bargeWakeSource, bargeWake);
            wakeActiveRef.current = true;
            sessionWakeActivatedRef.current = true;
            isActivelyListeningRef.current = true;
            setIsActivelyListening(true);
            setIsListening(true);
            if (after) {
              const coalesce = wakeCoalesceRef.current;
              coalesce.transcripts = [after];
              if (coalesce.timer) clearTimeout(coalesce.timer);
              coalesce.timer = setTimeout(submitWakeQuery, 600);
            } else {
              const bg = WAKE_GREETINGS[wakeCountRef.current % WAKE_GREETINGS.length];
              wakeCountRef.current += 1;
              makeAvatarSpeak(bg).catch(() => {});
              const coalesce = wakeCoalesceRef.current;
              if (coalesce.timer) clearTimeout(coalesce.timer);
              coalesce.timer = setTimeout(submitWakeQuery, 6000);
            }
          } else {
            // Direct barge-in question — no wake phrase required mid-conversation.
            // wakeActiveRef stays true — no need to say "Hey Mr. Edward Berwind" again after barge-in.
            const coalesce = wakeCoalesceRef.current;
            if (coalesce.timer) clearTimeout(coalesce.timer);
            coalesce.timer = null;
            coalesce.transcripts = [];
            recentSpeechRef.current = '';
            handleVoiceInput(t, { appendUserMessage: true }).catch((err) => console.error('Barge-in error:', err));
          }
          return;
        }
      }
      // ────────────────────────────────────────────────────────────────────────

      // Production mode: accept normal speech directly (wake phrase optional),
      // but ignore very short/noisy captures that often cause random popups.
      const transcriptWordCount = t.split(/\s+/).filter(Boolean).length;
      if (!wakeActiveRef.current && transcriptWordCount < 2) {
        return;
      }
      if (!wakeActiveRef.current) {
        wakeActiveRef.current = true;
        sessionWakeActivatedRef.current = true;
        isActivelyListeningRef.current = true;
        setIsActivelyListening(true);
        setIsListening(true);
      }

      // Wake already active: add this utterance to the question and (re)start the submit timer.
      // Ignore recognition results that fire while the avatar itself is talking (echo prevention).
      if (isAvatarTalkingRef.current) return;
      let cleaned = t.replace(/^(hey|hi|hello|ok|okay)\s*,?\s*(mr\.?\s*|mister\s*)?(berwind|berwin|bervind)\s*(of\s+the\s+elms)?[,\s]*/i, '').trim();
      // Android: cloud SR sometimes returns only a long mis-heard wake phrase; avoid dropping the whole utterance.
      if (!cleaned && isAndroid && t.replace(/\s+/g, ' ').trim().length >= 8) {
        cleaned = t.replace(/\s+/g, ' ').trim();
      }
      if (!cleaned) return;
      // Turn off the listening ring the moment user speech arrives.
      // Do NOT touch isTalking here — makeAvatarSpeak() may have already set it true
      // for a previous response before AVATAR_SPEAK_STARTED updates isAvatarTalkingRef.
      setIsListening(false);
      const coalesce = wakeCoalesceRef.current;
      const prevJoined = coalesce.transcripts.filter(Boolean).join(' ').trim();
      if (!prevJoined) {
        coalesce.transcripts.push(cleaned);
      } else if (cleaned === prevJoined || (cleaned.length >= prevJoined.length && cleaned.startsWith(prevJoined))) {
        coalesce.transcripts = [cleaned];
      } else {
        coalesce.transcripts.push(cleaned);
      }
      if (coalesce.timer) clearTimeout(coalesce.timer);
      // 3 s window: Android SR restarts take ~300 ms + the user may pause mid-sentence.
      // If a second SR session captures more words within 3 s, the timer resets and
      // the full phrase is collected before submission.
      coalesce.timer = setTimeout(submitWakeQuery, 3000);
    };

    recognition.onerror = (event) => {
      // Chrome/Android always fires error "aborted" after recognition.stop() (pause, session end, etc.).
      // It is not a failure — onend runs next and handles cleanup / restart. Do not reset UI here
      // or log as console.error (avoids duplicate state churn with onend).
      if (event.error === 'aborted') {
        log('Recognition aborted (normal — after stop/pause)');
        androidSrDebugLog('SR aborted + onend (expected when pausing SR)');
        return;
      }

      // After recognition.stop(), a replaced instance may still emit errors — ignore stale ones.
      if (recognitionRef.current !== recognition) return;

      // Android: no-speech is routine; onend follows and debounced-restart handles it. Skip console.error noise.
      if (isAndroid && event.error === 'no-speech') {
        log('No speech detected');
        androidSrDebugLog('SR no-speech');
        return;
      }

      console.error('Speech recognition error:', event.error);
      androidSrDebugLog(`SR error: ${event.error || 'unknown'}`);

      // Do NOT call recognition.stop() here — SR ends on its own after an error,
      // triggering onend which handles the restart. Calling stop() manually on Android
      // adds an extra mic-system round-trip, generating duplicate mic on/off sounds.
      isActivelyListeningRef.current = false;
      setIsActivelyListening(false);
      setIsListening(false);

      if (event.error === 'no-speech') {
        log('No speech detected');
      } else if (event.error === 'not-allowed') {
        showVoicePermissionHelp('both'); // Resolved by actual mic state: denied → mic popup, granted → SR popup
      } else if (event.error === 'network') {
        if (isDesktop) {
          alert('Voice recognition needs internet. Please check your connection and try again.');
        } else if (isAndroid && sessionActiveRef.current && !isMicMutedRef.current) {
          // Mobile Chrome: transient network blips to Google speech servers are common; retry once.
          setTimeout(() => {
            if (!sessionActiveRef.current || isMicMutedRef.current || isAvatarTalkingRef.current) return;
            try { startSpeechRecognition(); } catch (_) {}
          }, 900);
        }
      } else if (event.error === 'audio-capture') {
        showVoicePermissionHelp('microphone');
      }
    };

    recognition.onend = () => {
      // A newer SpeechRecognition instance may already be active — do not clear its state or schedule duplicate starts.
      if (recognitionRef.current !== recognition) {
        androidSrDebugLog('SR onend (stale instance, ignored)');
        return;
      }

      log('Speech recognition ended');
      androidSrDebugLog('SR onend');
      setIsListening(false);
      isActivelyListeningRef.current = false;
      setIsActivelyListening(false);

      if (!sessionActiveRef.current || isMicMutedRef.current) return;

      // Avatar speaking and we reached here means the mic is NOT muted (the
      // isMicMutedRef guard above returns first during auto-mute) — i.e. the user
      // tapped Unmute to barge in. If SR died, defer the restart to
      // AVATAR_SPEAK_ENDED to avoid extra restart churn; the toggle already restarts
      // SR on unmute, so this is only a rare mid-barge-in recycle.
      if (isAvatarTalkingRef.current) {
        if (isAndroid) ensureAndroidKeepAliveHealthy();
        log('SR onend while avatar speaking — defer restart to AVATAR_SPEAK_ENDED');
        androidSrDebugLog('SR onend during avatar speech → wait for SPEAK_ENDED');
        return;
      }

      // Android: debounce rapid onend (silence / internal recycle) so one recognition.start() follows many ends.
      // SR cycling is what drives repeated OS mic enable/disable sounds.
      if (isAndroid) {
        ensureAndroidKeepAliveHealthy();
        if (androidSrOnEndDebounceTimerRef.current) {
          clearTimeout(androidSrOnEndDebounceTimerRef.current);
        }
        androidSrOnEndDebounceTimerRef.current = setTimeout(() => {
          androidSrOnEndDebounceTimerRef.current = null;
          if (!sessionActiveRef.current || isMicMutedRef.current || isAvatarTalkingRef.current) return;
          if (isActivelyListeningRef.current) return;
          const go = () => {
            if (!sessionActiveRef.current || isMicMutedRef.current || isAvatarTalkingRef.current) return;
            if (androidShouldGateSrWithAudioMonitor()) startAudioMonitoring();
            else startSpeechRecognition();
          };
          requestAnimationFrame(() => setTimeout(go, 90));
        }, 420);
        return;
      }

      // Non-Android: restart SR immediately so wake-word detection keeps working continuously.
      const restartDelay = wakeActiveRef.current ? 0 : 100;

      setTimeout(() => {
        if (!sessionActiveRef.current || isMicMutedRef.current) return;
        if (isActivelyListeningRef.current) return; // Another SR already started (race guard)
        try {
          if (isAndroid) {
            // Fresh object every restart — Android Chrome can't reliably restart a stopped instance.
            recognitionRef.current = initializeSpeechRecognition();
            if (!recognitionRef.current) return;
          }
          activeListeningStartTimeRef.current = Date.now();
          isActivelyListeningRef.current = true;
          setIsActivelyListening(true);
          setIsListening(Boolean(wakeActiveRef.current));
          (isAndroid ? recognitionRef.current : recognition).start();
        } catch (e) {
          isActivelyListeningRef.current = false;
          setIsActivelyListening(false);
          setIsListening(false);
          // Secondary retry with a longer back-off
          const retryDelay = isAndroid ? 400 : 500;
          setTimeout(() => {
            if (!sessionActiveRef.current || isActivelyListeningRef.current || isMicMutedRef.current) return;
            try {
              if (isAndroid) recognitionRef.current = initializeSpeechRecognition();
              if (!recognitionRef.current) return;
              activeListeningStartTimeRef.current = Date.now();
              isActivelyListeningRef.current = true;
              setIsActivelyListening(true);
              setIsListening(Boolean(wakeActiveRef.current));
              recognitionRef.current.start();
            } catch (err) {
              log('Recognition restart failed:', err?.message);
              setIsListening(false);
            }
          }, retryDelay);
        }
      }, restartDelay);
    };

    return recognition;
  };

  const startBrowserSpeechRecognition = () => {
    // Do not start if user has muted the mic
    if (isMicMutedRef.current) {
      androidSrDebugLog('startSR skipped: mic muted');
      return;
    }
    if (!sessionActiveRef.current) {
      androidSrDebugLog('startSR skipped: no session');
      return;
    }
    // If already running, do nothing (we want always-on listening for wake phrase)
    if (isActivelyListeningRef.current) {
      androidSrDebugLog('startSR skipped: already active');
      return;
    }
    ensureAndroidKeepAliveHealthy();

    // On Android, always create a fresh recognition object — stopped instances cannot be restarted
    // reliably on Android Chrome, and VAD may call this after the previous object has ended.
    if (isAndroid || !recognitionRef.current) {
      recognitionRef.current = initializeSpeechRecognition();
    }

    if (!recognitionRef.current) {
      androidSrDebugLog('startSR failed: SpeechRecognition API missing');
      alert('Speech recognition is not supported in your browser. Please use text input instead.');
      return;
    }

    try {
      androidSrDebugLog('SR recognition.start() called');
      activeListeningStartTimeRef.current = Date.now();
      isActivelyListeningRef.current = true;
      setIsActivelyListening(true);
      setIsListening(Boolean(wakeActiveRef.current)); // Quiet wake listening unless wake is active
      recognitionRef.current.start();
    } catch (error) {
      androidSrDebugLog(`SR start() threw: ${error?.message || error}`);
      console.error('Error starting speech recognition:', error);
      isActivelyListeningRef.current = false;
      setIsActivelyListening(false);
      setIsListening(false);
      if (isAndroid && sessionActiveRef.current && !isMicMutedRef.current) {
        recognitionRef.current = null;
        setTimeout(() => {
          if (!sessionActiveRef.current || isMicMutedRef.current || isActivelyListeningRef.current) return;
          if (isAvatarTalkingRef.current) return;
          try { startBrowserSpeechRecognition(); } catch (_) {}
        }, 620);
      }
    }
  };

  const startSpeechRecognition = () => {
    startBrowserSpeechRecognition();
  };

  // Stop speech recognition. soft=true keeps mic stream alive to reduce access sounds.
  const stopSpeechRecognition = (soft = false) => {
    stopAudioMonitoring(); // cancel any pending audio-level poll
    if (androidSrOnEndDebounceTimerRef.current) {
      clearTimeout(androidSrOnEndDebounceTimerRef.current);
      androidSrOnEndDebounceTimerRef.current = null;
    }
    if (recognitionRef.current) {
      try {
        // Stop recognition immediately
        recognitionRef.current.stop();
      } catch (error) {
        // Ignore errors if already stopped or not started
        log('Recognition stop attempt:', error.message);
      }
    }
    // Always reset SR UI state; soft stop keeps wake/coalesce so watchdog/mute does not break mid-session listening.
    isActivelyListeningRef.current = false;
    setIsActivelyListening(false);
    setIsListening(false);
    setIsUserSpeaking(false);
    const wc = wakeCoalesceRef.current;
    if (wc.timer) clearTimeout(wc.timer);
    wc.timer = null;
    wc.transcripts = [];
    if (!soft) {
      wakeActiveRef.current = false;
      sessionWakeActivatedRef.current = false;
      recentSpeechRef.current = '';
    }
  };

  // Toggle mic mute so avatar does not capture background conversations.
  // Mute = close the software gate (discard results). We never stop/restart
  // recognition just to mute, so the phone never plays its mic-focus beep.
  const handleToggleMicMute = () => {
    trackButtonClick('toggle_mic_mute', { page: 'avatar' });
    if (isMicPermissionDenied || !hasMicDevice) return;
    const next = !micGateRef.current;
    micGateRef.current = next;
    isMicMutedRef.current = next;
    setIsMicMuted(next);
    manualMuteRef.current = next;      // user's explicit choice overrides auto-mute
    autoMutedForSpeechRef.current = false;

    if (next) {
      // Clear any half-captured speech buffers so nothing submits after unmute.
      const coalesce = wakeCoalesceRef.current;
      if (coalesce?.timer) clearTimeout(coalesce.timer);
      if (coalesce) { coalesce.timer = null; coalesce.transcripts = []; }
      recentSpeechRef.current = '';
    } else if (sessionActiveRef.current && !isActivelyListeningRef.current) {
      // Recognition normally never stops for muting; only restart if it actually died
      // (e.g. an Android no-speech recycle happened while muted). This also powers
      // barge-in: tapping Unmute while the avatar speaks reopens the gate and, if
      // needed, brings recognition back so the user can interrupt.
      androidShouldGateSrWithAudioMonitor() ? startAudioMonitoring() : startSpeechRecognition();
    }
  };
  const micButtonLabel = !hasMicDevice || isMicPermissionDenied ? 'Unavailable' : (isMicMuted ? 'Muted' : '');

  // Handle voice input (same as typed message)
  const handleVoiceInput = async (text, options = {}) => {
    const { appendUserMessage = false } = options;
    if (!text || !text.trim()) return;
    if (!sessionActiveRef.current) return;
    if (shouldSkipDuplicateSubmit(text)) return;
    if (isResponseInFlightRef.current) return;
    if (appendUserMessage) addChatMessage(text, 'user');
    isResponseInFlightRef.current = true;
    if (isGoodbyeMessage(text)) {
      pendingGoodbyeDisconnectRef.current = true;
    } else {
      pendingGoodbyeDisconnectRef.current = false;
    }
    pushDataLayerEvent('voice_query_submitted', {
      page: 'avatar',
      text_length: String(text.trim().length),
      is_goodbye: Boolean(isGoodbyeMessage(text)),
    });

    let timeoutId = null;
    try {
      setIsThinking(true);

      const controller = new AbortController();
      voiceAbortControllerRef.current = controller;
      timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text.trim(),
          history: chatMessages.slice(-10).map(msg => ({
            role: msg.role === 'user' ? 'user' : 'model',
            text: msg.content
          })),
          provider: 'auto'
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      voiceAbortControllerRef.current = null;

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get AI response');
      }

      const data = await response.json();
      let aiResponse = data.response;

      if (!aiResponse || !aiResponse.trim()) {
        console.error('❌ AI response is empty or invalid');
        addChatMessage('Error: No response from AI', 'avatar');
        setIsTalking(false);
        return;
      }

      addChatMessage(aiResponse, 'avatar');
      pushDataLayerEvent('voice_reply_received', {
        page: 'avatar',
        reply_length: String(aiResponse.trim().length),
      });
      makeAvatarSpeak(aiResponse).catch((err) => {
        console.error('Error making avatar speak:', err);
      });
    } catch (error) {
      console.error('Voice input error:', error);
      if (error.name === 'AbortError') {
        pendingGoodbyeDisconnectRef.current = false;
        return;
      }
      pendingGoodbyeDisconnectRef.current = false;
      pushDataLayerEvent('voice_query_failed', {
        page: 'avatar',
        error_name: String(error?.name || 'unknown'),
        error_message: String(error?.message || 'unknown'),
      });
      addChatMessage(`Error: ${error.message}`, 'avatar');
    } finally {
      clearTimeout(timeoutId);
      voiceAbortControllerRef.current = null;
      isResponseInFlightRef.current = false;
      // If speech is about to start, AVATAR_SPEAK_STARTED will clear Thinking.
      if (!isAvatarTalkingRef.current) setIsThinking(false);
    }
  };

  // Always-current submit function — updated every render so VAD and SR closures stay in sync.
  // Call via submitWakeQueryRef.current() from any callback (VAD, SR, timers).
  submitWakeQueryRef.current = () => {
    const coalesce = wakeCoalesceRef.current;
    if (coalesce.timer) clearTimeout(coalesce.timer);
    coalesce.timer = null;
    const merged = coalesce.transcripts.filter(Boolean).join(' ').trim();
    coalesce.transcripts = [];
    setIsUserSpeaking(false);
    // Wake-once-per-session: keep wakeActiveRef = true so the user never needs to
    // say "Hey Mr. Edward Berwind" again. AVATAR_SPEAK_ENDED will restore isListening after each reply.
    setIsListening(false); // ring off while AI is processing / avatar is about to speak
    if (!merged) {
      androidSrDebugLog('submitWakeQuery: empty merged → no chat send');
      // Empty timer (e.g. 7 s after greeting): keep UX + mic path alive like iOS continuous SR.
      if (wakeActiveRef.current && sessionActiveRef.current && !isMicMutedRef.current) {
        setIsListening(true);
        if (!isActivelyListeningRef.current) {
          setTimeout(() => {
            if (!sessionActiveRef.current || isMicMutedRef.current || isAvatarTalkingRef.current) return;
            androidShouldGateSrWithAudioMonitor() ? startAudioMonitoring() : startSpeechRecognition();
          }, 0);
        }
      }
      return;
    }
    androidSrDebugLog(`submitWakeQuery → chat (${merged.length} chars)`);
    touchSessionActivityRef.current();
    handleVoiceInput(merged, { appendUserMessage: true }).catch((err) => console.error('Voice query error:', err));
  };

  // Handle "Session not found" from LiveAvatar SDK – show message and allow reconnect
  const handleSessionNotFound = (err) => {
    const msg = err?.message || String(err);
    if (/session not found|Session not found/i.test(msg)) {
      setIsTalking(false);
      setIsThinking(false);
      setErrorMessage('Session expired or invalid. Please click Stop then Start again.');
      setErrorType('GENERAL');
      setIsConnected(false);
      isConnectedRef.current = false;
      setSessionActive(false);
      return true;
    }
    return false;
  };

  // Make avatar speak using LiveAvatar's own voice
  const makeAvatarSpeak = async (aiResponse) => {
    if (!avatarInstanceRef.current) {
      console.error('❌ Avatar session not available');
      return;
    }

    const session = avatarInstanceRef.current;
    
    // Wait for session to be CONNECTED and stream ready (avoids "Session not found" from speaking too early)
    let retries = 0;
    while (session.state !== SessionState.CONNECTED && retries < 50) {
      await new Promise(resolve => setTimeout(resolve, 100));
      retries++;
    }
    if (session.state !== SessionState.CONNECTED) {
      console.error('❌ Session not in CONNECTED state:', session.state);
      return;
    }
    retries = 0;
    while (!isStreamReadyRef.current && retries < 100) {
      await new Promise(resolve => setTimeout(resolve, 100));
      retries++;
    }
    if (!isStreamReadyRef.current) {
      console.warn('⚠️ Stream not ready yet; speaking anyway (may get Session not found)');
    }

    const sessionId = sessionIdRef.current || session._sessionInfo?.session_id || session.sessionId;
    // sessionId only needed for /api/task fallback – don't block TTS or repeat()

    try {
      setIsTalking(true);
      // If SDK speak-ended callback does not arrive, avoid getting stuck forever in "Speaking…".
      if (speakWatchdogRef.current) clearTimeout(speakWatchdogRef.current);
      speakWatchdogRef.current = setTimeout(() => {
        setIsTalking(false);
        isAvatarTalkingRef.current = false;
        speakWatchdogRef.current = null;
      }, 15000);
      const text = aiResponse.trim();
      if (!text) return;
      if (videoRef.current) {
        videoRef.current.muted = false;
        if (typeof videoRef.current.volume === 'number') videoRef.current.volume = AVATAR_OUTPUT_VOLUME;
      }
      // repeat() = AVATAR_SPEAK_TEXT (avatar speaks this text); message() = AVATAR_SPEAK_RESPONSE
      if (typeof session.repeat === 'function') {
        try {
          await session.repeat(text);
          return;
        } catch (repeatError) {
          console.error('[Avatar] repeat() failed:', repeatError?.message, repeatError);
          if (handleSessionNotFound(repeatError)) return;
        }
      }
      if (typeof session.message === 'function') {
        try {
          await session.message(text);
          return;
        } catch (msgError) {
          console.error('[Avatar] message() failed:', msgError?.message, msgError);
          if (handleSessionNotFound(msgError)) return;
        }
      }
      if (sessionId) {
        try {
          const taskResponse = await fetch('/api/task', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: sessionId, text, task_type: 'talk' }),
          });
          if (taskResponse.ok) return;
          console.warn('[Avatar] /api/task non-ok:', taskResponse.status);
        } catch (apiError) {
          console.error('[Avatar] /api/task error:', apiError?.message, apiError);
        }
      }
      console.error('[Avatar] All speech methods failed – avatar may not speak');
      setIsTalking(false);
    } catch (speakError) {
      console.error('[Avatar] Error making avatar speak:', speakError?.message, speakError);
      setIsTalking(false);
    }
  };

  // Show voice permission help popup – single "both" message for clarity (one set of steps for all users)
  const showVoicePermissionHelp = (type) => {
    if (voicePermissionHelpShownRef.current) return;
    if (Date.now() - lastMicGrantedAtRef.current < 20000) return;
    // Audio monitor running means we are actively watching for speech — not stuck.
    if (isAndroid && audioMonitorRef.current) return;
    if (type === 'microphone' || micPermissionStateRef.current === 'denied') {
      micPermissionStateRef.current = 'denied';
      setIsMicPermissionDenied(true);
    }
    voicePermissionHelpShownRef.current = true;
    setVoicePermissionHelpType('both');
    setVoicePermissionHelpVisible(true);
  };

  // Send message handler - Uses AI for responses then speaks via LiveAvatar
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    if (!chatInput.trim() || !sessionActive) return;

    touchSessionActivity();
    const messageText = chatInput.trim();
    if (shouldSkipDuplicateSubmit(messageText)) return;
    if (isResponseInFlightRef.current) return;
    isResponseInFlightRef.current = true;
    if (isGoodbyeMessage(messageText)) {
      pendingGoodbyeDisconnectRef.current = true;
    } else {
      pendingGoodbyeDisconnectRef.current = false;
    }
    pushDataLayerEvent('text_query_submitted', {
      page: 'avatar',
      text_length: String(messageText.length),
      is_goodbye: Boolean(isGoodbyeMessage(messageText)),
    });
    addChatMessage(messageText, 'user');
    setChatInput('');
    setIsThinking(true);

    const historyWithNew = [
      ...chatMessages.map(msg => ({ role: msg.role === 'user' ? 'user' : 'model', text: msg.content })),
    ].slice(-10);

    const isFirstQuestion = chatMessages.filter(msg => msg.role === 'assistant').length === 0;

    const controller = new AbortController();
    chatAbortControllerRef.current = controller;

    try {
      // Full response then speak via LiveAvatar
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: messageText, history: historyWithNew, provider: 'auto' }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get AI response');
      }

      const data = await response.json();
      let aiResponse = data.response;

      if (!aiResponse || !aiResponse.trim()) {
        console.error('❌ AI response is empty or invalid');
        addChatMessage('Error: No response from AI', 'avatar');
        return;
      }

      if (isFirstQuestion) {
        aiResponse = "My fellow citizens, I greet you from across time. Bring forth your questions, and together we shall seek clarity, truth, and thoughtful counsel.";
      }

      addChatMessage(aiResponse, 'avatar');
      pushDataLayerEvent('text_reply_received', {
        page: 'avatar',
        reply_length: String(aiResponse.trim().length),
      });
      makeAvatarSpeak(aiResponse).catch((err) => {
        console.error('Error making avatar speak (text input):', err);
      });
      
    } catch (error) {
      if (error.name === 'AbortError') {
        pendingGoodbyeDisconnectRef.current = false;
        return;
      }
      pendingGoodbyeDisconnectRef.current = false;
      pushDataLayerEvent('text_query_failed', {
        page: 'avatar',
        error_name: String(error?.name || 'unknown'),
        error_message: String(error?.message || 'unknown'),
      });
      console.error('Message send error:', error);
      addChatMessage(`Error: ${error.message}`, 'avatar');
    } finally {
      chatAbortControllerRef.current = null;
      isResponseInFlightRef.current = false;
      setIsThinking(false);
    }
  };

  // Save chat history for this session (local realtime; Google Sheets when session ended)
  const saveChatHistory = async (messages, sessionId, startedAt, endedAt) => {
    const list = Array.isArray(messages) ? messages : [];
    const started = startedAt || sessionStartTimeRef.current || new Date().toISOString();
    try {
      const res = await fetch('/api/chat-history', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionId || `local-${Date.now()}`,
          startedAt: started,
          endedAt: endedAt != null ? endedAt : null,
          avatarName: SESSION_SAVE_NAME,
          messages: list.map((m) => ({
            role: m.role,
            content: m.content,
            timestamp: m.timestamp || null,
          })),
        }),
      });
      if (!res.ok) {
        const errText = await res.text();
        warn('Chat history save failed:', res.status, errText?.slice(0, 200));
      }
    } catch (e) {
      warn('Could not save chat history:', e?.message || e);
    }
  };

  // Reset idle timer while session is active; clear when session ends.
  useEffect(() => {
    if (!sessionActive) {
      clearSessionIdleTimer();
      return undefined;
    }
    touchSessionActivity();
    return () => clearSessionIdleTimer();
  }, [sessionActive, touchSessionActivity, clearSessionIdleTimer]);

  // When session ends (active → inactive), clear chat input and release microphone access
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const wasActive = prevSessionActiveRef.current;
    prevSessionActiveRef.current = sessionActive;
    if (wasActive && !sessionActive) {
      stopSpeechRecognition(true); // Soft: keep mic stream to reduce re-prompt on next session
      setChatInput('');
      setVoicePermissionHelpType('both');
      setVoicePermissionHelpVisible(false);
    }
  }, [sessionActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Realtime save: persist chat when messages change (debounced 800ms to reduce lag)
  useEffect(() => {
    if (!sessionActive || chatMessages.length === 0) return;
    const sid = sessionIdRef.current;
    const started = sessionStartTimeRef.current;
    if (!sid || !started) return;
    const t = setTimeout(() => {
      saveChatHistory(chatMessages, sid, started, null);
    }, 800);
    return () => clearTimeout(t);
  }, [sessionActive, chatMessages]);

  // Close session handler (save only once per session)
  const handleClose = async () => {
    if (isClosingRef.current) return;
    pushDataLayerEvent('session_end_attempt', { page: 'avatar' });
    isClosingRef.current = true;
    setIsEndingSession(true);
    clearSessionIdleTimer();
    // Ensure transient mic-help UI is cleared immediately when user ends session.
    setVoicePermissionHelpVisible(false);
    setVoicePermissionHelpType('both');
    try {
      if (speakWatchdogRef.current) {
        clearTimeout(speakWatchdogRef.current);
        speakWatchdogRef.current = null;
      }
      // Mark session inactive immediately so mic auto-restart logic stops
      sessionActiveRef.current = false;

      // Immediately stop any ongoing avatar speech
      stopAvatarNarration();

      // Stop speech recognition and release microphone access
      stopSpeechRecognition();
      // Release Android keep-alive mic stream
      stopMicKeepAlive();
      // Reset greeting rotation counter for the next session
      wakeCountRef.current = 0;

      const sid = sessionIdRef.current;
      const started = sessionStartTimeRef.current;
      const ended = new Date().toISOString();
      if (!hasSavedThisSessionRef.current) {
        hasSavedThisSessionRef.current = true;
        await saveChatHistory(chatMessagesRef.current, sid, started, ended);
      }

      if (avatarInstanceRef.current) {
        await avatarInstanceRef.current.stop();
      }
      setSessionActive(false);
      setIsConnected(false);
      isConnectedRef.current = false;
      setMode(null);
      setChatMessages([]);
      sessionStartTimeRef.current = null;
      pushDataLayerEvent('session_end_success', { page: 'avatar' });
    } catch (error) {
      pushDataLayerEvent('session_end_failed', {
        page: 'avatar',
        error_name: String(error?.name || 'unknown'),
        error_message: String(error?.message || 'unknown'),
      });
      console.error('Session close error:', error);
    } finally {
      setIsEndingSession(false);
      isClosingRef.current = false;
    }
  };

  // Keep handleCloseRef updated so AVATAR_SPEAK_ENDED can call it for goodbye auto-disconnect
  handleCloseRef.current = handleClose;

  // Cleanup on unmount – release avatar and mic stream
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    return () => {
      if (avatarAudioRecoveryTimeoutRef.current) {
        clearTimeout(avatarAudioRecoveryTimeoutRef.current);
        avatarAudioRecoveryTimeoutRef.current = null;
      }
      if (avatarInstanceRef.current && sessionActive) {
        avatarInstanceRef.current.stop().catch(() => {});
      }
      stopMicKeepAlive();
    };
  }, [sessionActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // WebView: force inline playback so video doesn't go fullscreen (iOS/Android app WebView)
  useEffect(() => {
    const t = setTimeout(() => {
      const el = videoRef.current;
      if (el) {
        el.setAttribute('playsinline', '');
        el.setAttribute('webkit-playsinline', '');
      }
    }, 0);
    return () => clearTimeout(t);
  }, []);

  // Re-attach video when session becomes active (ensures avatar shows if stream was ready before video ref)
  useEffect(() => {
    if (!sessionActive || !avatarInstanceRef.current || !videoRef.current) return;
    const session = avatarInstanceRef.current;
    try {
      session.attach(videoRef.current);
      if (videoRef.current.paused) {
        videoRef.current.play().then(() => {
          setVideoReady(true);
        }).catch(() => {});
      } else {
        setVideoReady(true);
      }
    } catch (e) {
      warn('Video re-attach:', e?.message);
    }
  }, [sessionActive]);

  // Keep-alive not required for default FULL mode; LiveAvatar manages session lifetime.

  // Auto-scroll chat within the messages container only (no page/body scroll)
  useEffect(() => {
    const el = chatMessagesContainerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [chatMessages]);

  // Stop avatar narration (TTS audio + HeyGen interrupt) – e.g. when user clicks mic
  const stopAvatarNarration = () => {
    const audio = ttsAudioRef.current;
    if (audio) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (_) {}
      ttsAudioRef.current = null;
    }
    setIsTalking(false);
    isAvatarTalkingRef.current = false;
    if (videoRef.current) {
      videoRef.current.muted = false;
      if (typeof videoRef.current.volume === 'number') videoRef.current.volume = AVATAR_OUTPUT_VOLUME;
      try {
        const playPromise = videoRef.current.play?.();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      } catch (_) {}
    }
    const session = avatarInstanceRef.current;
    if (session && typeof session.interrupt === 'function') {
      try {
        session.interrupt();
      } catch (_) {}
    }
  };

  // Siri-style interaction: wake phrase handles barge-in and listening (no tap-to-interrupt UI)

  // Handle send button
  const handleSend = () => {
    if (chatInput.trim() && sessionActive && !isTalking) {
      trackButtonClick('send_message', { page: 'avatar', input_method: 'button_or_enter' });
      handleSendMessage();
    }
  };

  // Map messages for display (convert role to match design expectations)
  const messages = chatMessages.map(msg => ({
    ...msg,
    role: msg.role === 'assistant' ? 'assistant' : 'user'
  }));

  // On mobile, pin layout to visual viewport so it doesn't shift when keyboard opens.
  // Desktop uses normal layout flow.
  const layoutStyle = isMobile ? visualViewportStyle : undefined;

  return (
    <div className={`avatar-layout flex flex-col h-[100dvh] max-h-[100dvh] w-full max-w-[100vw] min-w-0 bg-white md:bg-slate-100 overflow-hidden ${isMobile ? 'touch-none overscroll-none' : ''}`} style={layoutStyle}>
      {/* ── Apple-Intelligence glow ring (SVG-based, single element, works on Android) ──
           Old approach: div + CSS conic-gradient + mask-composite → broke on Android Chrome
           (mask failed silently → solid colour overlay covering the avatar = "double glow").
           SVG <rect> strokes ARE rings by nature — no masking needed.
           Speaking: rainbow hue-spin. Listening: solid sky-blue pulse. */}
      {isMobile && isTalking && (
        <svg
          className="glow-ring-speak"
          aria-hidden="true"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{ position: 'fixed', inset: 0, width: '100%', height: '100dvh', pointerEvents: 'none', zIndex: 10000, overflow: 'visible' }}
        >
          <defs>
            <filter id="gr-bloom-speak" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="2.2" result="blur" />
            </filter>
          </defs>
          {/* Soft bloom – thick blurred stroke */}
          <rect x="0" y="0" width="100" height="100" fill="none"
            stroke="rgba(90,200,250,0.65)" strokeWidth="14"
            rx="2.2" vectorEffect="non-scaling-stroke"
            filter="url(#gr-bloom-speak)" />
          {/* Sharp ring */}
          <rect x="0" y="0" width="100" height="100" fill="none"
            stroke="rgba(90,200,250,1)" strokeWidth="5"
            rx="2.2" vectorEffect="non-scaling-stroke" />
        </svg>
      )}
      {isMobile && isListening && !isTalking && (
        <svg
          className="glow-ring-listen"
          aria-hidden="true"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{ position: 'fixed', inset: 0, width: '100%', height: '100dvh', pointerEvents: 'none', zIndex: 10000, overflow: 'visible' }}
        >
          <defs>
            <filter id="gr-bloom-listen" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="2.2" result="blur" />
            </filter>
          </defs>
          {/* Soft bloom */}
          <rect x="0" y="0" width="100" height="100" fill="none"
            stroke="rgba(56,189,248,0.60)" strokeWidth="12"
            rx="2.2" vectorEffect="non-scaling-stroke"
            filter="url(#gr-bloom-listen)" />
          {/* Sharp ring */}
          <rect x="0" y="0" width="100" height="100" fill="none"
            stroke="rgba(56,189,248,1)" strokeWidth="4"
            rx="2.2" vectorEffect="non-scaling-stroke" />
        </svg>
      )}

      {/* User journey / onboarding – first-time app intro */}
      {showUserJourney && (
        <UserJourney
          mode={userJourneyMode}
          onDismiss={() => setShowUserJourney(false)}
        />
      )}
      {/* Legal disclaimer – show every time before starting session */}
      {showLegalBeforeStart && (
        <div className="legal-disclaimer-overlay" role="dialog" aria-modal="true" aria-labelledby="legal-before-start-title">
          <div className="legal-disclaimer-modal relative">
            <button
              type="button"
              onClick={() => setShowLegalBeforeStart(false)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-100"
              aria-label="Close legal disclaimer"
            >
              <X className="w-4 h-4" />
            </button>
            <h2 id="legal-before-start-title" className="legal-disclaimer-modal__title">
              Legal disclaimer
            </h2>
            <p className="legal-disclaimer-modal__text">{DISCLAIMER_TEXT}</p>
            <label className="legal-disclaimer-modal__consent">
              <input
                type="checkbox"
                checked={legalConsentChecked}
                onChange={(e) => setLegalConsentChecked(e.target.checked)}
              />
              <span>
                I agree to the <a href="/terms-of-service">Terms of Service</a> and{' '}
                <a href="/privacy-policy">Privacy Policy</a>.
              </span>
            </label>
            <button
              type="button"
              onClick={() => {
                if (!legalConsentChecked) return;
                trackButtonClick('legal_agree_start', { page: 'avatar' });
                try {
                  window.localStorage.setItem('afterlife-bot-legal-accepted', 'true');
                } catch (e) {}
                setShowLegalBeforeStart(false);
                handleStart();
              }}
              className="legal-disclaimer-modal__button"
              aria-label="I agree to the disclaimer"
              disabled={!legalConsentChecked}
            >
              I Agree
            </button>
          </div>
        </div>
      )}
      {isEndingSession && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black/80">
          <div className="w-10 h-10 border-2 border-white/70 border-t-transparent rounded-full animate-spin mb-3" />
          <div className="text-white text-sm font-medium">Ending session…</div>
          <div className="text-white/70 text-xs mt-1">Please wait</div>
        </div>
      )}
      {/* Header: fixed at top; high z-index so visible in app WebView above video */}
      <header className="flex-shrink-0 z-[9998] fixed left-0 right-0 top-0 flex items-center justify-center gap-2 sm:gap-5 md:gap-0 py-1.5 sm:py-4 md:py-3 px-3 pt-[max(0.375rem,env(safe-area-inset-top))] sm:pt-4 sm:px-6 md:px-10 bg-white border-b border-slate-200 md:grid md:grid-cols-[200px_1fr_200px] md:items-center">
        <img
          src={LOGO_URL}
          alt="Afterlife Bot"
          className="w-[120px] sm:w-[150px] md:w-[200px] h-auto flex-shrink-0 object-contain rounded md:justify-self-start"
        />
        <div className="text-center min-w-0 flex-1 overflow-hidden md:justify-self-center">
          <h1 className="text-base sm:text-lg md:text-xl font-serif text-slate-800 tracking-wider break-words line-clamp-2">
            {APP_NAME}
          </h1>
          <p className="text-[0.5em] sm:text-[0.62em] md:text-[0.72em] text-slate-500 uppercase tracking-[0.08em] sm:tracking-[0.12em] font-light whitespace-nowrap">
            {APP_SUBTITLE}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            trackButtonClick('open_info_menu', { page: 'avatar' });
            setShowInfoMenu((v) => !v);
          }}
          className="w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:text-sky-600 hover:bg-sky-50 flex-shrink-0 md:justify-self-end"
          aria-label="Open app information menu"
        >
          <Info className="w-5 h-5" />
        </button>
      </header>
      {showInfoMenu && (
        <div className="fixed inset-0 z-[9997]" onClick={() => setShowInfoMenu(false)}>
          <div
            className="absolute right-3 sm:right-6 md:right-10 top-[3.4rem] sm:top-[4.5rem] md:top-[5rem] w-[220px] rounded-xl border border-slate-200 bg-white shadow-xl p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="block px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-sky-50 hover:text-sky-700"
              onClick={() => {
                setShowInfoMenu(false);
                setInfoPopupPath('/about-app?popup=1');
              }}
            >
              About App
            </button>
            <button
              type="button"
              className="block px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-sky-50 hover:text-sky-700"
              onClick={() => {
                setShowInfoMenu(false);
                setInfoPopupPath('/terms-of-service?popup=1');
              }}
            >
              Terms and Conditions
            </button>
            <button
              type="button"
              className="block px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-sky-50 hover:text-sky-700"
              onClick={() => {
                setShowInfoMenu(false);
                setInfoPopupPath('/privacy-policy?popup=1');
              }}
            >
              Privacy
            </button>
          </div>
        </div>
      )}
      {infoPopupPath && (
        <div className="fixed inset-0 z-[9998] bg-black/60 flex items-center justify-center p-3 sm:p-6">
          <div className="relative w-full max-w-4xl h-[85vh] bg-white rounded-xl shadow-2xl overflow-hidden border border-slate-200">
            <button
              type="button"
              onClick={() => setInfoPopupPath('')}
              className="absolute top-2 right-2 z-10 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center"
              aria-label="Close information popup"
            >
              <X className="w-5 h-5" />
            </button>
            <iframe
              src={infoPopupPath}
              title="Information popup"
              className="w-full h-full border-0"
            />
          </div>
        </div>
      )}

      {/* Spacer so content starts below fixed header (compact on mobile to maximize avatar, no bottom gap) */}
      <div className="flex-shrink-0 h-[3rem] md:h-[5.5rem]" aria-hidden="true" />
      {/* Main content: video | chat – fills to bottom on mobile, no white space */}
      <div className="desktop-main-shell flex-1 flex flex-col md:flex-row md:items-stretch min-h-0 min-w-0 overflow-hidden pt-0 md:pt-3 max-md:pb-0 md:mx-auto md:mt-2 md:mb-3 md:w-[min(1280px,calc(100%-2rem))] md:p-4 md:rounded-2xl md:border md:border-slate-200 md:bg-white md:shadow-sm">
      {/* Video panel: fill screen on mobile; avatar frame 9:16 */}
      <div className={`avatar-video-panel relative flex-1 min-w-0 flex flex-col overflow-hidden max-md:overflow-x-hidden max-md:min-h-0 px-2 md:px-5 max-md:px-1 max-md:pb-0 md:self-stretch md:h-full max-md:flex-1 max-md:min-h-0 ${
        !isMobile && !chatPanelVisible
          ? 'md:min-w-0 md:max-w-[720px] md:flex-[1_1_auto] md:mx-auto'
          : 'md:min-w-[320px] md:max-w-[460px] md:flex-[0_0_38%]'
      } ${
        chatPanelVisible && !mobileChatExpanded ? 'max-md:flex-[0_0_48%]' : ''
      }`}>
        <div className="avatar-stage relative flex-1 flex items-center justify-center bg-transparent min-h-[180px] rounded-lg md:rounded-2xl overflow-hidden min-w-0">
          {/* 9:16 avatar frame – keep min height so avatar/image always visible when embedded or via URL */}
          <div className="relative w-full h-full flex items-center justify-center min-h-0 min-w-0">
            <div
              className="avatar-frame-shell relative aspect-[9/16] h-full w-auto max-w-full min-h-[200px] md:max-h-none bg-transparent rounded-lg md:rounded-xl overflow-hidden"
              onKeyDown={(e) => {
                // no-op: interaction is voice-first ("Hey Mr. Edward Berwind")
              }}
              role={undefined}
              tabIndex={undefined}
              aria-label={undefined}
            >
              {/* Show static Mr. Edward Berwind image any time the live stream is not yet active */}
              {!sessionActive && (
                <>
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: 'url(/aftelifebot_berwind_background.jpeg)' }}
                  />
                </>
              )}
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted={!sessionActive}
                controls={false}
                className="avatar-video-element absolute inset-0 w-full h-full object-cover"
                style={{ visibility: sessionActive && videoReady ? 'visible' : 'hidden' }}
                preload="auto"
                aria-label="Avatar video"
                data-playsinline
                data-webview-inline
              />
            </div>
          </div>
          {/* Status badges moved to bottom above buttons */}
          {isConnecting && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70">
              <div className="w-10 h-10 border-2 border-sky-400 border-t-transparent rounded-full animate-spin mb-2" />
              <span className="text-sm text-white/80">Connecting...</span>
            </div>
          )}
          {errorType && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 p-5 text-center rounded-lg">
              <div className="text-3xl mb-3">⚠️</div>
              <h3 className="text-base font-medium text-white mb-2">Connection issue</h3>
              <p className="text-sm text-white/80 mb-5 max-w-md leading-relaxed">{errorMessage}</p>
              <div className="flex gap-3">
                <button onClick={handleStaticMode} className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white text-sm rounded">Archive</button>
                <button onClick={handleStart} className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white text-sm rounded">Retry</button>
              </div>
            </div>
          )}
          {!isConnecting && !errorType && !sessionActive && (
            <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
              <button
                onClick={() => {
                  trackButtonClick('start_session_cta', { page: 'avatar' });
                  setShowLegalBeforeStart(true);
                }}
                disabled={isConnecting}
                className="px-6 py-2.5 bg-sky-600 hover:bg-sky-500 text-white text-sm rounded-lg font-medium disabled:opacity-50"
                aria-label="Start session"
              >
                {isConnecting ? 'Starting...' : 'Start session'}
              </button>
            </div>
          )}

          {/* Voice permission help – show only while a live session is active */}
          {voicePermissionHelpVisible && sessionActive && !isEndingSession && (
            <div className="voice-permission-help absolute inset-x-3 bottom-[max(4rem,calc(env(safe-area-inset-bottom)+4rem))] md:bottom-[7.5rem] z-[40] rounded-xl bg-black/80 backdrop-blur-xl text-white px-3 py-3 shadow-lg border border-white/10">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium mb-1">Mr. Edward Berwind&apos;s ears are plugged!</p>
                <button
                  type="button"
                  onClick={() => setVoicePermissionHelpVisible(false)}
                  className="w-6 h-6 -mt-0.5 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10"
                  aria-label="Close voice permission help"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {!isMobile ? (
                <p className="text-xs text-white/95 leading-relaxed mt-1">
                  Microphone not enabled, so Mr. Edward Berwind cannot hear you right now. You can still continue the conversation by typing your questions in the chat panel.
                </p>
              ) : isAndroid ? (
                <ul className="text-xs text-white/95 leading-relaxed mt-1 list-disc list-inside space-y-0.5">
                  <li>End session first.</li>
                  <li>Go to Chrome → Settings → Site settings → find this site.</li>
                  <li>Allow or reset Microphone for the matching URL (delete permission if needed, then set to Allow).</li>
                  <li>Close the tab and open the site again.</li>
                  <li>Start a new session and allow microphone when prompted.</li>
                </ul>
              ) : (
                <ul className="text-xs text-white/95 leading-relaxed mt-1 list-disc list-inside space-y-0.5">
                  <li>End session first.</li>
                  <li>Go to Settings → Privacy &amp; Security.</li>
                  <li>Enable Speech Recognition and Microphone for your browser.</li>
                  <li>Start a new session and allow microphone when prompted.</li>
                  <li>If both are already enabled: End session, start a new session, and allow microphone when prompted.</li>
                </ul>
              )}
            </div>
          )}

          {/* Bottom: status bar + mic mute (left) + End session (right) */}
          {sessionActive && (
            <>
              <div className="session-controls-wrap absolute bottom-[max(0.25rem,env(safe-area-inset-bottom))] left-2 right-2 md:left-4 md:right-4 md:bottom-6 z-30 flex flex-col items-stretch gap-3">
              {/* Listening / Thinking / Speaking status above buttons */}
              <div className="session-status-row flex justify-center items-center text-center w-full">
                {isTalking ? (
                  <span className="session-status-label status-label-pulse text-white/95 text-[16px] md:text-[20px] font-semibold tracking-wide">
                    Speaking…
                  </span>
                ) : isThinking ? (
                  <span className="session-status-label status-label-pulse text-white/95 text-[16px] md:text-[20px] font-semibold tracking-wide">
                    Thinking…
                  </span>
                ) : isUserSpeaking ? (
                  <span className="session-status-label status-label-pulse text-white/95 text-[16px] md:text-[20px] font-semibold tracking-wide">
                    Listening…
                  </span>
                ) : sessionActive ? (
                  <span className="session-status-label status-label-pulse text-white/95 text-[16px] md:text-[20px] font-semibold tracking-wide">
                    Waiting for your question…
                  </span>
                ) : null}
              </div>
              {/* Muted hint: shown while the mic is muted (auto or manual) so the user knows to tap to talk */}
              {sessionActive && isMicMuted && hasMicDevice && !isMicPermissionDenied && (
                <div className="self-start mb-2 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-sm text-white text-xs font-medium flex items-center gap-1.5">
                  <MicOff className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="whitespace-nowrap">Tap to speak</span>
                </div>
              )}
              {/* Mute (left) + End session (right) – compact pill button that never clips */}
              <div className="session-button-row flex flex-row items-center justify-between gap-3 w-full min-w-0">
                <button
                  type="button"
                  onClick={handleToggleMicMute}
                  className={`session-mute-btn min-w-[46px] min-h-[46px] md:min-w-[52px] md:min-h-[52px] px-3 w-auto h-12 md:h-14 rounded-full flex items-center justify-center ${micButtonLabel ? 'gap-2' : 'gap-0'} border-2 text-sm font-medium flex-shrink-0 order-first ${
                    isMicMuted
                      ? 'bg-slate-800 text-white border-slate-700'
                      : 'bg-white text-slate-800 border-slate-300'
                  }`}
                  aria-pressed={isMicMuted}
                  aria-label={!hasMicDevice || isMicPermissionDenied ? 'Microphone not enabled' : (isMicMuted ? 'Unmute microphone' : 'Mute microphone')}
                >
                  {isMicMuted ? (
                    <MicOff className="w-5 h-5 md:w-6 md:h-6" />
                  ) : (
                    <Mic className="w-5 h-5 md:w-6 md:h-6" />
                  )}
                  {micButtonLabel && <span className="text-xs font-semibold tracking-wide">{micButtonLabel}</span>}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    trackButtonClick('end_session', { page: 'avatar' });
                    handleClose();
                  }}
                  disabled={isEndingSession}
                  className="session-end-btn flex-shrink-0 max-w-[70%] min-w-0 min-h-[40px] md:min-h-[44px] px-4 sm:px-5 py-2 md:py-2.5 rounded-full bg-red-600 flex items-center justify-center hover:bg-red-500 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-red-600 text-white text-xs sm:text-sm font-medium whitespace-nowrap overflow-hidden order-last"
                  aria-label="End session"
                >
                  <span className="truncate">End session</span>
                </button>
              </div>
            </div>
            </>
          )}
        </div>
      </div>

      {/* Chat panel: desktop only */}
      {!isMobile && chatPanelVisible && (
        <div
          className={`chat-panel chat-panel-desktop flex flex-col min-h-0 min-w-0 bg-white max-md:overflow-x-hidden
            md:relative md:z-auto md:flex-[1_1_62%] md:min-w-[540px] md:max-w-none md:flex-shrink md:flex-grow-0 md:self-stretch md:rounded-xl md:border md:border-slate-300 md:border-r md:border-r-slate-300 md:overflow-hidden
            max-md:w-full max-md:max-w-[100vw] max-md:overflow-hidden max-md:rounded-t-2xl max-md:shadow-lg max-md:flex-shrink-0
            ${mobileChatExpanded ? 'max-md:fixed max-md:inset-0 max-md:z-[60] max-md:pt-[4.5rem]' : 'max-md:flex-1 max-md:mt-0'}
          `}
        >
          <div className={`chat-header flex-shrink-0 flex items-center justify-between px-3 py-2.5 sm:px-4 md:px-5 md:py-4 border-b border-slate-200 bg-white md:bg-slate-50 min-w-0 ${mobileChatExpanded ? 'max-md:sticky max-md:top-0 relative z-[70]' : ''}`}>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm sm:text-base md:text-lg font-medium md:font-semibold text-slate-900 truncate">Mr. Edward Berwind Live Chat</h2>
              <p className="hidden md:block text-xs text-slate-500 mt-0.5 truncate">
                Speak or type your questions. Responses are in the voice of Mr. Edward Berwind of the Elms of Newport, RI.
              </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={() => {
                  trackButtonClick('toggle_mobile_chat_expand', { page: 'avatar' });
                  setMobileChatExpanded((e) => !e);
                }}
                className="md:hidden w-9 h-9 rounded-full flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-600"
                aria-label={mobileChatExpanded ? 'Show split view' : 'Expand chat'}
              >
                {mobileChatExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
              </button>
              <button
                type="button"
                onClick={() => {
                  trackButtonClick('close_chat', { page: 'avatar' });
                  setChatPanelVisible(false);
                  setMobileChatExpanded(false);
                }}
                className="md:hidden w-8 h-8 rounded flex items-center justify-center bg-sky-100 hover:bg-sky-200 text-sky-700"
                aria-label="Close chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div ref={chatMessagesContainerRef} className="chat-messages-area flex-1 min-h-0 md:min-h-[180px] overflow-y-auto overflow-x-hidden px-3 py-4 sm:px-4 md:mx-4 md:mt-4 md:mb-4 bg-slate-50 md:bg-white overscroll-contain border border-slate-200 md:border-slate-300 md:border-r-2 md:border-r-slate-400 rounded-lg md:rounded-xl box-border w-full min-w-0">
            {messages.length === 0 && !isTalking ? (
              <div className="flex flex-col items-center justify-center gap-3 min-h-[200px] text-slate-500 text-sm text-center">
                <span>{sessionActive ? 'Send a message below to start the conversation.' : 'Start a session to chat.'}</span>
              </div>
            ) : (
              <div className="space-y-4 w-full min-w-0" style={{ width: '100%' }}>
                {messages.map((msg) => (
                  <div key={msg.id} className={`flex gap-2 w-full ${msg.role === 'user' ? 'justify-end chat-row-user' : 'justify-start'}`} style={msg.role === 'user' ? { width: '100%', minWidth: '100%' } : { width: '100%', minWidth: 0 }}>
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0 text-white text-sm font-semibold">
                        B
                      </div>
                    )}
                    {msg.role === 'user' ? (
                      <>
                        <div className="flex-1 min-w-0" aria-hidden="true" />
                        <div
                          className="chat-bubble-user max-w-[85%] min-w-[3rem] px-3 py-2 rounded-lg flex-shrink-0 bg-white border border-slate-200 text-slate-800 shadow-sm"
                          data-role="user"
                        >
                          <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">{msg.content || ''}</p>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-sky-400 flex items-center justify-center flex-shrink-0 text-white text-xs font-semibold" aria-hidden="true">U</div>
                      </>
                    ) : (
                      <div
                        className="max-w-[85%] min-w-0 px-3 py-2 rounded-lg flex-shrink-0 bg-white border border-slate-200 text-slate-800"
                        data-role="assistant"
                      >
                        <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">{msg.content || ''}</p>
                      </div>
                    )}
                  </div>
                ))}
                {isTalking && (
                  <div className="flex gap-2">
                    <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0 text-white text-sm font-semibold">
                      B
                    </div>
                    <div className="px-3 py-2 rounded-lg bg-white border border-slate-200 flex gap-1">
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}
                {isTalking && (
                  <p className="text-xs text-slate-500 italic mt-1">{AVATAR_DISPLAY_NAME} is typing…</p>
                )}
                <div ref={chatEndRef} />
              </div>
            )}
          </div>
          {/* Input bar: rounded input + Send, no mic controls */}
          <div className="chat-input-bar flex-shrink-0 p-3 md:px-4 md:pt-3 md:pb-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-slate-200 bg-white md:bg-slate-50 box-border">
            <div className="chat-input-row">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Ask Mr. Edward Berwind anything..."
                disabled={!sessionActive}
                className="flex-1 min-w-0 px-3 py-3 rounded-full border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-400 disabled:opacity-50 bg-white"
                aria-label="Message input"
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={!chatInput.trim() || !sessionActive}
                className="chat-send-btn rounded-full bg-red-500 hover:bg-red-600 disabled:bg-slate-300 flex items-center justify-center text-white shadow-md md:bg-sky-500 md:hover:bg-sky-600"
                aria-label="Send message"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Footer disclaimer – hidden on mobile so app fits screen without scrolling */}
      <footer className="flex-shrink-0 min-w-0 py-2 px-3 md:px-4 bg-slate-50 border-t border-slate-200 max-md:hidden">
        <p className="text-center text-xs text-slate-500 max-w-2xl mx-auto break-words overflow-hidden">
          {FOOTER_TEXT}
        </p>
      </footer>
    </div>
  );
}

export default AvatarComponent;

